import { supabase } from './supabase';
import app from './firebase';
// @ts-ignore
import { getAuth } from 'firebase/auth';
import { detectStructuralType, calculateDetailedGoal, calculateDetailedProgress, isVal, getNormalizedData, pick, normalizeStr, normalizeSectionContent as legacyNormalize } from './planning-logic';
import { serializeSection } from './planning-contract';
import { computeSectionMetrics } from './planning-engine/calculateSectionMetrics';
import { resolveCanonicalSectionNumber, normalizeSectionContent } from './planning-engine/normalizeSection';
import { normalize, normalizeVPType } from './shared-logic';
import {
    requireStructureResponsibleRecords,
    StructureResponsiblesLoadError
} from './structure-responsibles';
import { isConfirmedGovUserDeletion } from './gov-user-deletion';

// HOTFIX: v18.24.5-SYNC-ENGINE-S5-TOLERANT-DRY-RUN-HOTFIX aplicado para dry-run tolerante de S5.
export { supabase };

/**
 * SANITIZACIÓN DE CADENAS (v16.3.0)
 * Limpia y normaliza cadenas de texto para evitar mojibake y asegurar integridad UTF-8.
 * Preserva tildes, eñes y caracteres especiales válidos según requerimiento v16.3.0.
 */
export const GOV_USER_SAFE_SELECT = 'id, email, role, vicepresidency_id, commission_id, name, full_access, created_at, accessible_modules, org_scope, accessible_instances, accessible_vps';

export const sanitizeUTF8 = (str: any): string => {
    if (str === null || str === undefined) return "";
    let clean = String(str).trim();
    // Mapa de corrección para mojibake común detectado en auditoría
    const corrections: Record<string, string> = {
        'á': 'á', 'é': 'é', 'í': 'í', 'ó': 'ó', 'ú': 'ú',
        'ñ': 'ñ', 'Ñ': 'Ñ', 'É': 'É', 'Ó': 'Ó', 'Ú': 'Ú',
        'Ã ': 'à', 'Ã¨': 'è', 'Ã¬': 'ì', 'Ã²': 'ò', 'Ã¹': 'ù',
        '¿': '¿', '¡': '¡', 'Â': '', 'Â ': ' ',
        'â€“': '–', 'â€”': '—', 'â€˜': "'", 'â€™': "'",
        'â€œ': '"', 'â€ ': '"', 'Ã‚': '',
        'ón': 'ón', 'án': 'án', 'én': 'én', 'ín': 'ín',
        'ún': 'ún', 'ña': 'ña', 'ño': 'ño'
    };
    
    Object.entries(corrections).forEach(([wrong, right]) => {
        clean = clean.split(wrong).join(right);
    });

    return clean;
};

// ─── Tipos ───────────────────────────────────────────────
export type UserRole = 'ADMINISTRADOR' | 'EDITOR' | 'VISOR' | 'VICE_PRESIDENT' | 'VICEPRESIDENTE' | 'COMMISSION_PRESIDENT' | 'COMMITTEE_PRESIDENT' | 'COUNSELOR' | 'SUPER_ADMIN' | 'COORDINADOR' | 'DIRECTOR_NACIONAL';

export const PROTECTED_COMMISSIONS = ['48bdf7be-b0d5-4024-8298-ed2c38bf3125']; // Bienestar Social

export interface User {
    id: string; // Este será el UUID de auth.users
    name: string;
    email: string;
    role: UserRole;
    password?: string; // Solo para creación/edición manual
    vicepresidency_id?: string | null;
    commission_id?: string | null;
    accessible_modules?: string[] | null;
    org_scope?: 'GLOBAL' | 'VP' | 'INSTANCE' | 'COORDINATOR' | null;
    instance_id?: string | null;
    accessible_instances?: string[] | null;
    accessible_vps?: string[] | null;
}


export type AuthSession = {
    user: User | null;
    session: any;
}

export interface Vicepresidency {
    id: string;
    name: string;
    president_name: string;
}

export interface Commission {
    id: string;
    name: string;
    type: 'COMISION' | 'COMITE' | 'CONSEJERIA';
    vicepresidency_id: string | null;
    president_name: string;
    status: string;
    peso?: number;
}

type DeleteCommissionActor = Pick<User, 'role'> | null | undefined;

export interface DeleteCommissionDependencySummary {
    work_plans: number;
    users: number;
    monthly_reports: number;
    indicators: number;
    meetings: number;
}

export interface DeleteCommissionResult {
    deleted: boolean;
    deleted_id: string;
    dependency_summary: DeleteCommissionDependencySummary;
}

export interface WorkPlan {
    id: string;
    commission_id: string;
    year: number;
    status: string;
    created_at: string;
    updated_at?: string;
    commission_name?: string;
    commission_type?: string;
    total_goal?: number;
    total_progress?: number;
    kpi_compliance?: number;
    vp_approved?: boolean;
    indicators_president_approved?: boolean;
    sections_count?: number;
    live_total_goal?: number;
    live_total_progress?: number;
}

export interface WorkPlanSection {
    id: string;
    work_plan_id: string;
    section_number: number;
    name: string;
    order_index: number;
    min_goal: number;
    goal: number;
    progress: number;
    content: any;
}

export interface TalentRecord {
    id: string;
    user_id: string;
    name: string;
    email: string;
    role: string;
    education: string;
    experience: string;
    interests: string;
    areas?: string[];
    experience_level?: number;
    projects?: string;
    other_expertise?: string;
}

export interface Meeting {
    id: string;
    owner_id: string;
    vicepresidency_id?: string | null;
    commission_id?: string | null;
    title: string;
    date: string;
    agenda: {
        title: string;
        duration: number;
        responsible: string;
        type: string;
    }[];
    status: 'PLANNED' | 'COMPLETED' | 'CANCELLED';
    summary?: string;
}

export interface DocRecord {
    id: string;
    commission_id: string;
    commission_name?: string;
    title: string;
    type: string;
    url: string;
    content?: any;
    gov_commission_id?: string;
    category?: string;
    source_type?: 'FILE' | 'EXTERNAL_LINK' | 'INTERNAL';
    status?: 'ACTIVE' | 'ARCHIVED';
    revision?: number;
    archived_at?: string | null;
    archived_by?: string | null;
    acl_mode?: 'INHERIT_SCOPE' | 'RESTRICTED';
    mime_type?: string | null;
    created_at: string;
}




export interface MonthlyReport {
    id: string;
    commission_id: string;
    commission_name?: string;
    month: number;
    year: number;
    status: string;
}

export interface CommissionDashboard {
    commission_id: string;
    commission_name: string;
    commission_type: string;
    vicepresidency_id: string | null;
    plan_status: string;
    total_sections: number;
    filled_sections: number;
    total_goal: number;
    total_progress: number;
    pct_cumplimiento: number;
    kpi_green: number;
    kpi_amber: number;
    kpi_red: number;
}

export interface PresidenciaDashboard {
    total_commissions: number;
    approved_plans: number;
    pending_plans: number;
    total_goal: number;
    total_progress: number;
    pct_cumplimiento: number;
    kpi_green: number;
    kpi_amber: number;
    kpi_red: number;
}

// Eliminada interface SemaphoreSettings por redundancia con SystemSettings v16.9.3

export interface VPDashboard {
    vicepresidency_id: string;
    vp_name: string;
    president_name: string;
    total_commissions: number;
    approved_plans: number;
    pending_plans: number;
    pct_cumplimiento: number;
    kpi_green: number;
    kpi_amber: number;
    kpi_red: number;
}

export interface PresidenciaDashboard {
    total_commissions: number;
    approved_plans: number;
    pending_plans: number;
    pct_cumplimiento: number;
    kpi_green: number;
    kpi_amber: number;
    kpi_red: number;
    independent_commissions: number;
}

/**
 * Obtiene la lista completa de usuarios registrados en el sistema.
 * @returns {Promise<User[]>} Lista de usuarios.
 */
export const getUsers = async (): Promise<User[]> => {
    const users = await fetchAllScopedGovUserRows<User>('admin_user_list', 'users');
    return users.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
};

export class GovScopedReadError extends Error {
    status: number | null;

    constructor(message: string, status: number | null = null) {
        super(message);
        this.name = 'GovScopedReadError';
        this.status = status;
    }
}

const getFunctionErrorStatus = (error: any): number | null => {
    const status = Number(error?.context?.status);
    return Number.isInteger(status) && status >= 400 ? status : null;
};

export const getScopedReadErrorMessage = (error: unknown): string => {
    const status = error instanceof GovScopedReadError ? error.status : null;
    if (status === 401) return 'La sesion segura expiro. Cierra sesion e ingresa nuevamente.';
    if (status === 403) return 'Tu perfil no tiene un alcance de lectura valido para estos datos.';
    return 'No fue posible recuperar los datos. La informacion visible no se reemplazo por valores vacios.';
};

export const isScopedReadNotFound = (error: unknown): boolean =>
    error instanceof GovScopedReadError && error.status === 404;

const requireScopedArray = <T>(value: unknown, field: string): T[] => {
    if (!Array.isArray(value)) {
        throw new GovScopedReadError(`Invalid scoped response: ${field}`);
    }
    return value as T[];
};

type GovUsersScopedAction = 'talent_directory' | 'admin_user_list';
type GovUsersScopedField = 'talents' | 'users';

type GovUsersScopedPagination = {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
};

const invokeGovSecureFunction = async <T extends Record<string, any>>(
    functionName: string,
    body: Record<string, any> = {}
): Promise<T> => {
    const secureToken = getGovSecureTokenOrThrow();
    const { data, error } = await supabase.functions.invoke(functionName, {
        headers: {
            'x-gov-secure-token': secureToken
        },
        body
    });

    if (error) {
        throw new GovScopedReadError(error.message || 'Error del servidor', getFunctionErrorStatus(error));
    }

    if (!data?.success) {
        throw new GovScopedReadError(data?.error || 'Error del servidor');
    }

    return data as T;
};

export type GovMutationOptions = {
    idempotencyKey?: string;
    dryRun?: boolean;
};

export const createMutationIdempotencyKey = (): string => crypto.randomUUID();

export const getMutationErrorMessage = (error: unknown): string => {
    const status = error instanceof GovScopedReadError ? error.status : null;
    if (status === 400) return 'Los datos o permisos enviados no son validos. Revisa los cambios e intenta nuevamente.';
    if (status === 401) return 'La sesion segura expiro. Cierra sesion e ingresa nuevamente.';
    if (status === 403) return 'Tu perfil no tiene permisos para realizar esta operacion.';
    if (status === 404) return 'El registro ya no existe o no esta disponible.';
    if (status === 409) return 'La operacion entra en conflicto con datos existentes o dependencias activas.';
    return 'No fue posible completar la operacion segura. No se confirmaron cambios.';
};

const invokeGovMutation = async <T extends Record<string, any>>(
    functionName: 'manage-gov-talent-scoped' | 'manage-gov-users-scoped' | 'manage-gov-planning-scoped' | 'manage-gov-tracking-scoped',
    body: Record<string, any>,
    options: GovMutationOptions = {}
): Promise<T> => invokeGovSecureFunction<T>(functionName, {
    ...body,
    idempotency_key: options.idempotencyKey || createMutationIdempotencyKey(),
    dry_run: options.dryRun === true
});

const fetchAllScopedGovUserRows = async <T>(
    action: GovUsersScopedAction,
    field: GovUsersScopedField
): Promise<T[]> => {
    const rows: T[] = [];
    let page = 1;
    let totalPages = 1;

    do {
        const response = await invokeGovSecureFunction<{
            success: true;
            users?: T[];
            talents?: T[];
            pagination: GovUsersScopedPagination;
        }>('get-gov-users-scoped', { action, page, limit: 100 });
        rows.push(...requireScopedArray<T>(response[field], field));

        const pagination = response.pagination;
        if (
            !pagination
            || pagination.page !== page
            || pagination.limit !== 100
            || !Number.isInteger(pagination.total_pages)
            || pagination.total_pages < 1
            || pagination.total_pages > 20
        ) {
            throw new GovScopedReadError('Invalid scoped response: pagination');
        }
        totalPages = pagination.total_pages;
        page += 1;
    } while (page <= totalPages);

    return rows;
};

const fetchScopedWorkPlans = async (options: {
    year?: number;
    commissionIds?: string[];
    vicepresidencyId?: string | null;
    includeSectionCounts?: boolean;
    includeLiveTotals?: boolean;
} = {}): Promise<any[]> => {
    const response = await invokeGovSecureFunction<{ success: true; plans: any[] }>('get-work-plans-scoped', {
        year: options.year,
        commission_ids: options.commissionIds,
        vicepresidency_id: options.vicepresidencyId,
        include_section_counts: options.includeSectionCounts === true,
        include_live_totals: options.includeLiveTotals === true
    });

    return requireScopedArray(response.plans, 'plans');
};

const fetchScopedWorkPlanDetailRaw = async (
    planId: string,
    options: { includeItems?: boolean } = {}
): Promise<any> => {
    const response = await invokeGovSecureFunction<{ success: true; plan: any }>('get-work-plan-detail-scoped', {
        plan_id: planId,
        include_items: options.includeItems === true
    });

    if (!response.plan || typeof response.plan !== 'object') {
        throw new GovScopedReadError('Invalid scoped response: plan');
    }
    return response.plan;
};

const fetchScopedIndicatorsRaw = async (options: {
    instanceIds?: string[];
    indicatorIds?: string[];
} = {}): Promise<any[]> => {
    const response = await invokeGovSecureFunction<{ success: true; indicators: any[] }>('get-indicators-scoped', {
        instance_ids: options.instanceIds,
        indicator_ids: options.indicatorIds
    });

    return requireScopedArray(response.indicators, 'indicators');
};

export const getTrackingReportScoped = async (options: {
    instanceId: string;
    month: number;
    year: number;
    format: string;
}): Promise<any | null> => {
    const response = await invokeGovSecureFunction<{ success: true; report: any | null }>('manage-gov-tracking-scoped', {
        action: 'get_report',
        instance_id: options.instanceId,
        month: options.month,
        year: options.year,
        format: options.format
    });
    return response.report || null;
};

export const saveTrackingReportScoped = async (instanceId: string, report: Record<string, any>) =>
    invokeGovMutation('manage-gov-tracking-scoped', {
        action: 'save_report',
        instance_id: instanceId,
        report
    });

export const recordGovClientEventScoped = async (event: Record<string, any>) =>
    invokeGovSecureFunction('manage-gov-direct-writes-scoped', { action: 'client_event', event });

const mapScopedWorkPlans = (plans: any[]): WorkPlan[] => {
    return (plans || []).map((plan: any) => ({
        ...plan,
        commission_name: plan.gov_commissions?.name || '',
        commission_type: plan.gov_commissions?.type || '',
    }));
};

/**
 * Obtiene el usuario actualmente autenticado desde Supabase Auth
 */
export const getCurrentSession = async (): Promise<AuthSession> => {
    try {
        const cachedUserStr = localStorage.getItem('gov_custom_session');
        const secureToken = localStorage.getItem('gov_secure_token');
        
        if (cachedUserStr && secureToken) {
            try {
                JSON.parse(cachedUserStr);
                const { data, error } = await supabase.functions.invoke('get-gov-session-profile', {
                    headers: { 'x-gov-secure-token': secureToken },
                    body: {}
                });
                if (error || !data?.success || !data?.user) {
                    localStorage.removeItem('gov_custom_session');
                    localStorage.removeItem('gov_secure_token');
                    return { user: null, session: null };
                }
                localStorage.setItem('gov_custom_session', JSON.stringify(data.user));
                return { user: data.user, session: { user: data.user, access_token: secureToken } };
            } catch(e) {
                localStorage.removeItem('gov_custom_session');
                localStorage.removeItem('gov_secure_token');
            }
        }

        if (cachedUserStr || secureToken) {
            localStorage.removeItem('gov_custom_session');
            localStorage.removeItem('gov_secure_token');
        }

        // LOCAL_CUSTOM requires the cached profile plus gov_secure_token.
        // A Firebase identity alone cannot authorize browser-side gov_users RLS.
        return { user: null, session: null };
    } catch(e) {
        return { user: null, session: null };
    }
};

type AuthInvokeFailure = 'credentials' | 'runtime' | 'reconciliation' | 'in_progress' | 'completed';

// Kept only in memory so a retry in this page reuses the server-side operation.
// The identifier is not a password, session token, or credential and is never persisted.
const pendingGovLoginOperationIds = new Map<string, string>();

const classifyAuthInvokeFailure = async (error: any): Promise<AuthInvokeFailure> => {
    const response = error?.context;
    if (response && typeof response.clone === 'function') {
        try {
            const payload = await response.clone().json();
            if (payload?.error === 'Invalid credentials') return 'credentials';
            if (payload?.code === 'AUTH_LOGIN_REQUIRES_RECONCILIATION') return 'completed';
            if (payload?.code === 'AUTH_RECONCILIATION_PENDING') return 'reconciliation';
            if (payload?.code === 'AUTH_LOGIN_IN_PROGRESS') return 'in_progress';
            if (/invalid jwt|jwt|api.?key/i.test(`${payload?.message || ''} ${payload?.error || ''}`)) {
                return 'runtime';
            }
        } catch {
            // A non-JSON gateway response is an infrastructure failure, not a password verdict.
        }
    }

    const message = String(error?.message || '');
    if (/invalid credentials/i.test(message)) return 'credentials';
    return 'runtime';
};

export const signIn = async (email: string, password: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    let govSecureToken: string | null = null;
    let sessionUser: User | null = null;
    let authFailure: AuthInvokeFailure | null = null;
    let operationId = pendingGovLoginOperationIds.get(normalizedEmail);
    if (!operationId) {
        operationId = crypto.randomUUID();
        while (pendingGovLoginOperationIds.size >= 8) {
            const oldestEmail = pendingGovLoginOperationIds.keys().next().value;
            if (!oldestEmail) break;
            pendingGovLoginOperationIds.delete(oldestEmail);
        }
        pendingGovLoginOperationIds.set(normalizedEmail, operationId);
    }

    try {
        const { data, error } = await supabase.functions.invoke('create-gov-auth-session', {
            body: { email: normalizedEmail, password, operation_id: operationId }
        });

        if (!error && data?.success && data?.token && data?.user) {
            govSecureToken = data.token;
            sessionUser = data.user as User;
            pendingGovLoginOperationIds.delete(normalizedEmail);
        } else {
            authFailure = error ? await classifyAuthInvokeFailure(error) : 'runtime';
            if (authFailure === 'credentials') pendingGovLoginOperationIds.delete(normalizedEmail);
            if (authFailure === 'completed') pendingGovLoginOperationIds.delete(normalizedEmail);
        }
    } catch (err: any) {
        authFailure = await classifyAuthInvokeFailure(err);
        if (authFailure === 'credentials' || authFailure === 'completed') {
            pendingGovLoginOperationIds.delete(normalizedEmail);
        }
        console.error("[AUTH] Edge authentication request failed.");
    }

    if (!govSecureToken || !sessionUser) {
        console.error("[AUTH] Edge auth did not return a secure token or user payload.");
        if (authFailure === 'runtime') {
            throw new Error("SERVICIO DE AUTENTICACION NO DISPONIBLE.");
        }
        if (authFailure === 'reconciliation' || authFailure === 'completed') {
            throw new Error("LA SESION REQUIERE CONCILIACION. CONTACTE AL ADMINISTRADOR.");
        }
        if (authFailure === 'in_progress') {
            throw new Error("EL INICIO DE SESION SIGUE EN CURSO. ESPERE Y REINTENTE.");
        }
        throw new Error("ACCESO DENEGADO. CREDENCIALES INVALIDAS.");
    }

    const session = {
        user: sessionUser,
        access_token: govSecureToken
    };

    localStorage.setItem('gov_custom_session', JSON.stringify(sessionUser));
    localStorage.setItem('gov_secure_token', govSecureToken);

    return { user: sessionUser, session, authFailed: false };
};

export const signOut = async () => {
    // Start revocation before clearing the local token, then report only confirmed outcomes.
    const customRevocation = invokeGovSecureFunction<{ revoked: boolean }>('revoke-gov-auth-session', {})
        .then(result => result.revoked === true)
        .catch(() => false);
    localStorage.removeItem('gov_custom_session');
    localStorage.removeItem('gov_secure_token');
    try {
        const auth = getAuth(app);
        await auth.signOut();
    } catch(e) {}
    const customSessionRevoked = await customRevocation;
    let nativeAuthRevoked = false;
    try {
        const { error } = await supabase.auth.signOut();
        nativeAuthRevoked = !error;
    } catch {
        nativeAuthRevoked = false;
    }
    if (!customSessionRevoked) console.warn('[AUTH] Custom session revocation is unconfirmed.');
    if (!nativeAuthRevoked) console.warn('[AUTH] Native Auth revocation is unconfirmed.');
    return { customSessionRevoked, nativeAuthRevoked };
};

export const getUser = async (id: string): Promise<User | null> => {
    try {
        const response = await invokeGovSecureFunction<{ success: true; user: User }>(
            'get-gov-session-profile', { user_id: id },
        );
        return response.user;
    } catch {
        return null;
    }
};

// ─── Vicepresidencias (CRUD completo) ────────────────────
export const getVicepresidencies = async (): Promise<Vicepresidency[]> => {
    const { data, error } = await supabase.from('gov_vicepresidencies').select('*');
    if (error) throw error;
    return (data || []).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
};

export const createVicepresidency = async (name: string, president_name?: string): Promise<string> => {
    const { data, error } = await supabase.from('gov_vicepresidencies')
        .insert({ name, president_name: president_name || '' })
        .select('id').single();
    if (error) throw error;
    return data.id;
};

export const updateVicepresidency = async (id: string, updates: Partial<Vicepresidency>) => {
    const { error } = await supabase.from('gov_vicepresidencies').update(updates).eq('id', id);
    if (error) throw error;
};

export const deleteVicepresidency = async (id: string) => {
    throw new Error('Eliminación de Vicepresidencias deshabilitada: requiere ruta administrativa certificada.');
};

// ─── Comisiones (con soporte para independientes) ────────
export const getCommissions = async (user: User): Promise<Commission[]> => {
    let query = supabase.from('gov_commissions').select('*');
    if (user.role === 'VICE_PRESIDENT' && user.vicepresidency_id) {
        query = query.eq('vicepresidency_id', user.vicepresidency_id);
    } else if (user.role === 'COMMISSION_PRESIDENT' && user.commission_id) {
        query = query.eq('id', user.commission_id);
    }
    const { data, error } = await query;
    if (error) throw error;
    return (data || []).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
};

export const getAllCommissions = async (): Promise<Commission[]> => {
    const { data, error } = await supabase.from('gov_commissions').select('*');
    if (error) throw error;
    return (data || []).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
};

export const createCommission = async (input: Partial<Commission>): Promise<string> => {
    const data = await invokeGovSecureFunction<{ commission: Commission }>('manage-gov-commissions-scoped', {
        action: 'create', changes: {
            name: input.name || '',
            type: input.type || 'COMISION',
            vicepresidency_id: input.vicepresidency_id || null,
            president_name: input.president_name || 'Sin asignar',
            status: 'ACTIVE',
        }
    });
    return data.commission.id;
};

export const updateCommission = async (id: string, updates: Partial<Commission>) => {
    await invokeGovSecureFunction('manage-gov-commissions-scoped', {
        action: 'update', commission_id: id, changes: updates
    });
};

const canActorDeleteCommission = (actor: any, targetCommissionId: string): boolean => {
    if (!actor) return false;
    const role = actor.role || '';
    if (role === 'SUPER_ADMIN' || role === 'ADMINISTRADOR' || role === 'DIRECTOR_NACIONAL') return true;
    if (role === 'EDITOR' || role === 'COORDINADOR') {
        const scope = actor.org_scope || (role === 'COORDINADOR' ? 'COORDINATOR' : 'INSTANCE');
        if (scope === 'INSTANCE' && (actor.commission_id === targetCommissionId || actor.instance_id === targetCommissionId)) {
            return true;
        }
        if (scope === 'COORDINATOR') {
            const instIds = actor.accessible_instances || [];
            if (instIds.includes(targetCommissionId)) return true;
        }
    }
    return false;
};

export const deleteCommission = async (
    id: string,
    options: boolean | { bypassShield?: boolean; actor?: any } = false
): Promise<DeleteCommissionResult> => {
    const actor = typeof options === 'boolean' ? null : options?.actor;
    
    if (!canActorDeleteCommission(actor, id)) {
        throw new Error('No tienes permiso para eliminar esta instancia o está fuera de tu ámbito de coordinación asignado.');
    }

    try {
        console.log("[DELETE_CHECK] delete_requested");
        const secureToken = localStorage.getItem('gov_secure_token');
        console.log(`[DELETE_CHECK] secure_token_exists: ${!!secureToken}`);

        if (!secureToken) {
            throw new Error('Por seguridad, el borrado requiere una sesión backend activa. Por favor, cierra sesión y vuelve a entrar con tu correo y contraseña.');
        }
        console.log("[DELETE_CHECK] fetch_started: true");
        const edgeFunctionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-structure-commission`;
        console.log(`[DELETE_CHECK] endpoint_url: ${edgeFunctionUrl}`);
        
        try {
            const response = await fetch(edgeFunctionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-gov-secure-token': secureToken,
                    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
                },
                body: JSON.stringify({ commission_id: id })
            });
            
            console.log(`[DELETE_CHECK] response_status: ${response.status}`);
            
            let data;
            try {
                data = await response.json();
                console.log(`[DELETE_CHECK] response_body:`, data);
            } catch (e) {
                console.error("[DELETE_CHECK] parse json error");
            }

            if (!response.ok) {
                throw new Error(data?.error || `Edge Function falló con status: ${response.status}`);
            }

            if (data && data.success) {
                console.log("[DELETE_CHECK] edge_success: true");
                return { deleted: true, deleted_id: id, dependency_summary: { work_plans: 0, users: 0, monthly_reports: 0, indicators: 0, meetings: 0 } };
            } else {
                throw new Error(data?.message || data?.error || 'Error desconocido al eliminar.');
            }
        } catch (fetchError: any) {
            console.error(`[DELETE_CHECK] fetch failed:`, fetchError);
            throw new Error(`FAILED TO FETCH: ${fetchError.name} - ${fetchError.message}. Endpoint: ${edgeFunctionUrl}`);
        }
    } catch (e: any) {
        console.error("[deleteCommission] Falla durante el borrado vía Supabase Edge Function:", e);
        if (e.message) {
            throw new Error(`Error del servidor: ${e.message}`);
        }
        throw e;
    }
};

/**
 * Genera la estructura jerárquica organizacional para el usuario actual.
 * Aplica el "Hierarchy Shield": Los Admins/VPs ven todo, otros solo su área.
 * 
 * @param {User} user Usuario que realiza la consulta.
 * @returns {Promise<{ vicepresidencies: Vicepresidency[], commissions: Commission[], committees: Commission[], councils: Commission[], independents: Commission[] }>} Estructura filtrada.
 */
export const getHierarchy = async (user: User) => {
    const hierarchyCommissionColumns = 'id, name, type, vicepresidency_id, president_name';
    const hierarchyVicepresidencyColumns = 'id, name, president_name';
    // Determinar si ve todo basándose en org_scope o rol legacy
    const isDirectorNacional = user.role === 'DIRECTOR_NACIONAL';
    const isAdmin = user.role === 'SUPER_ADMIN' || user.role === 'ADMINISTRADOR' || isDirectorNacional;
    
    // BLINDAJE NACIONAL: Los directores y admins siempre ven el panorama GLOBAL
    const scope = isAdmin ? 'GLOBAL' : (user.org_scope?.toUpperCase() || (
        user.role === 'COORDINADOR' ? 'COORDINATOR' : 
        ((user.role === 'VICE_PRESIDENT' || user.role === 'VICEPRESIDENTE' || (user.role === 'VISOR' && user.vicepresidency_id)) && user.vicepresidency_id) ? 'VP' : 
        'INSTANCE'
    ));
    const isFullView = scope === 'GLOBAL' || isAdmin; 

    const { data: allComms, error: commissionsError } = await supabase
        .from('gov_commissions')
        .select(hierarchyCommissionColumns);
    if (commissionsError) throw new StructureResponsiblesLoadError(commissionsError);
    const commsList = requireStructureResponsibleRecords(allComms, 'gov_commissions').map(c => ({
        ...c,
        status: '',
        name: c.name || `${c.type || 'INSTANCIA'} SIN NOMBRE`
    })).sort((a, b) => a.name.localeCompare(b.name));

    let vps: Vicepresidency[] = [];

    // Identificar ID activo (v15.26.0 Shield: Resolución robusta de VP para presidentes de instancia)
    let activeVpId: string | null = user.vicepresidency_id;
    let myCommId = user.instance_id || user.commission_id;

    if (!activeVpId && myCommId) {
        const myComm = commsList.find(c => c.id === myCommId);
        if (myComm?.vicepresidency_id) activeVpId = myComm.vicepresidency_id;
    }

    if (isFullView) {
        const { data, error: vicepresidenciesError } = await supabase
            .from('gov_vicepresidencies')
            .select(hierarchyVicepresidencyColumns);
        if (vicepresidenciesError) throw new StructureResponsiblesLoadError(vicepresidenciesError);
        vps = requireStructureResponsibleRecords(data, 'gov_vicepresidencies')
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else {
        const relevantVpIds = new Set<string>();
        if (activeVpId && (scope === 'VP' || scope === 'INSTANCE')) {
            relevantVpIds.add(activeVpId);
        }
        if (scope === 'COORDINATOR') {
            (user.accessible_vps || []).forEach((id: string) => relevantVpIds.add(id));
            // v16.9.14: También incluir VPs de las instancias explícitas para evitar que se pierdan en el agrupamiento jerárquico
            const instIds = user.accessible_instances || [];
            commsList.filter(c => instIds.includes(c.id) && c.vicepresidency_id).forEach(c => relevantVpIds.add(c.vicepresidency_id!));
        }
        
        if (relevantVpIds.size > 0) {
            const { data, error: vicepresidenciesError } = await supabase
                .from('gov_vicepresidencies')
                .select(hierarchyVicepresidencyColumns)
                .in('id', Array.from(relevantVpIds));
            if (vicepresidenciesError) throw new StructureResponsiblesLoadError(vicepresidenciesError);
            vps = requireStructureResponsibleRecords(data, 'gov_vicepresidencies')
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        }
    }

    // Lógica de filtrado de comisiones basada en el rol y el acceso asignado
    let filteredComms = commsList;
    if (!isFullView) {
        if (scope === 'INSTANCE' && myCommId) {
            filteredComms = commsList.filter(c => c.id === myCommId);
        } else if (scope === 'VP' && activeVpId) {
            filteredComms = commsList.filter(c => c.vicepresidency_id === activeVpId);
        } else if (scope === 'COORDINATOR') {
             const instIds = user.accessible_instances || [];
             const vpIds = user.accessible_vps || [];
             filteredComms = commsList.filter(c => 
                instIds.includes(c.id) || 
                (c.vicepresidency_id && vpIds.includes(c.vicepresidency_id))
             );
        } else {
            filteredComms = [];
        }
    }

    // Agrupar instancias por Vicepresidencia
    const vpsWithInstances = vps.map(vp => {
        // Blindaje estricto: solo mostramos instancias a las que el usuario tenga acceso real
        const vpComms = filteredComms.filter(c => c.vicepresidency_id === vp.id);

        return {
            ...vp,
            comisiones: vpComms.filter(c => normalize(c.type).includes('COMISION')),
            comites: vpComms.filter(c => normalize(c.type).includes('COMITE')),
            consejerias: vpComms.filter(c => normalize(c.type).includes('CONSEJERIA'))
        };
    });

    const topLevelVpInstances = filteredComms.filter(c => c.vicepresidency_id !== null);

    return {
        vicepresidencies: vpsWithInstances,
        // Compatibilidad legacy: "commissions" preserva todas las instancias ligadas a VP
        commissions: topLevelVpInstances,
        // Compatibilidad Tracking v18.27.60: aliases tipados top-level
        committees: topLevelVpInstances.filter(c => normalize(c.type).includes('COMITE')),
        councils: topLevelVpInstances.filter(c => normalize(c.type).includes('CONSEJERIA')),
        independents: filteredComms.filter(c => c.vicepresidency_id === null)
    };
};

/**
 * Recupera los planes de trabajo anuales permitidos para el usuario.
 * @param {User} user Usuario autenticado.
 * @returns {Promise<WorkPlan[]>} Colección de planes de trabajo con metadata de comisión.
 */
export const getVPImpactData = async (vpId: string) => {
    const { data: instances, error: e1 } = await supabase
        .from('gov_commissions')
        .select('*')
        .eq('vicepresidency_id', vpId)
        .eq('status', 'ACTIVE');
    
    if (e1) throw e1;

    const { data: settings } = await supabase.from('gov_settings').select('current_month').single();
    const monthIdx = settings?.current_month || new Date().getMonth();

    return {
        instances: (instances || []).map(i => ({...i, id: i.id, name: i.name, type: i.type})),
        monthIdx
    };
};

export const getNationalImpactData = async () => {
    const { data: instances, error: e1 } = await supabase
        .from('gov_commissions')
        .select('*')
        .eq('status', 'ACTIVE');
    
    if (e1) throw e1;

    const { data: settings } = await supabase.from('gov_settings').select('current_month').single();
    const monthIdx = settings?.current_month || new Date().getMonth();

    return {
        instances: (instances || []).map(i => ({...i, id: i.id, name: i.name, type: i.type, vicepresidency_id: i.vicepresidency_id})),
        monthIdx
    };
};

export const getWorkPlansLegacy = async (user: User): Promise<WorkPlan[]> => {
    return getWorkPlans(user);
};

export const getWorkPlans = async (user: User): Promise<WorkPlan[]> => {
    const requestedVpId = (
        user?.vicepresidency_id &&
        (user?.org_scope === 'VP' || user?.role === 'VICE_PRESIDENT' || user?.role === 'VICEPRESIDENTE')
    ) ? user.vicepresidency_id : null;

    const plans = await fetchScopedWorkPlans({
        vicepresidencyId: requestedVpId,
        includeSectionCounts: true
    });

    return mapScopedWorkPlans(plans);
};

export const getWorkPlanDetailLegacy = async (planId: string) => {
    return getWorkPlanDetail(planId);
};

export const getWorkPlanDetail = async (planId: string) => {
    const response = await invokeGovSecureFunction<{ success: true; plan: any }>('get-work-plan-detail-scoped', {
        plan_id: planId
    });
    const plan = response.plan;
    const rawSections = plan?.sections || [];

    const sections = (rawSections || []).map(rs => {
        const sn = resolveCanonicalSectionNumber(rs);
        const reconstructedContent = normalizeSectionContent(sn || Number(rs.section_number), rs.content);
        
        return {
            ...rs,
            name: rs.name,
            content: reconstructedContent,
            progress: rs.progress || 0
        };
    });

    return {
        ...plan,
        commission_name: plan.gov_commissions?.name || '',
        commission_type: plan.gov_commissions?.type || '',
        sections: sections || [],
    };
};

const DEFAULT_SECTIONS = [
    'Objetivos de Contribución al Plan Estratégico',
    'Diagnóstico (Sector, FODA e Intervenciones)',
    'Estructura Operativa y Vicepresidencias',
    'Comisiones, Comités o Consejerías Locales',
    'Agenda Mensual de Eventos y Actividades',
    'Temas Relevantes y Agenda Prioritaria',
    'Convenios Estratégicos y Alianzas',
    'Proyectos Estratégicos de Alto Impacto',
    'Convenios Adicionales y Enlaces',
    'Espacios de Representación y Organismos',
    'Sesiones de Consejo y Calendario',
];

export const createWorkPlan = async (user: User, year: number, commissionId?: string): Promise<string> => {
    const targetCommId = commissionId || user.commission_id;
    if (!targetCommId) throw new Error('Selecciona una comisión');

    const existing = await fetchScopedWorkPlans({
        commissionIds: [targetCommId],
        year
    });
    if (existing && existing.length > 0) return existing[0].id;

    const response = await invokeGovMutation<{ plan_id: string }>('manage-gov-planning-scoped', {
        action: 'create_plan',
        commission_id: targetCommId,
        year,
        section_names: DEFAULT_SECTIONS
    });
    return response.plan_id;
};

export const deleteWorkPlan = async (planId: string, bypassShield = false) => {
    const plan = await getWorkPlanDetail(planId);
    if (!bypassShield && plan && PROTECTED_COMMISSIONS.includes(plan.commission_id)) {
        throw new Error('PROTECCIÓN DE SEGURIDAD ACTIVADA: El Plan de Trabajo de Bienestar Social está blindado contra eliminaciones accidentales.');
    }
    await invokeGovMutation('manage-gov-planning-scoped', { action: 'delete_plan', plan_id: planId });
};

export const updateWorkPlanSection = async (sectionId: string, updates: any) => {
    // ═══ BLINDAJE v18.12.02: PARIDAD GOAL <-> META_ANUAL ═══
    // Garantiza que cualquier actualización de meta se refleje en ambos campos persistidos
    if (updates.goal !== undefined) {
        updates.meta_anual = Number(updates.goal);
    } else if (updates.meta_anual !== undefined) {
        updates.goal = Number(updates.meta_anual);
    }
    
    await invokeGovMutation('manage-gov-planning-scoped', {
        action: 'update_section',
        section_id: sectionId,
        changes: updates
    });
};

// ─── Dashboards (cascada via SQL views) ──────────────────
/**
 * Obtiene las métricas consolidadas para el tablero de la Presidencial Nacional.
 * Realiza cálculos de agregación sobre comisiones y estados de planes.
 * 
 * @returns {Promise<PresidenciaDashboard>} Estadísticas globales de gobernanza.
 */
export const getDashboardPresidencia = async (): Promise<PresidenciaDashboard> => {
    const { data, error } = await supabase.from('gov_v_presidencia_dashboard').select('*').single();
    if (error) throw error;
    return data;
};

export const getDashboardByVP = async (): Promise<VPDashboard[]> => {
    const { data, error } = await supabase.from('gov_v_vp_dashboard').select('*').order('vp_name');
    if (error) throw error;
    return data || [];
};

// ─── Configuración y Pesos (Admin Only Visibility in UI) ───

// Funciones obsoletas eliminadas para unificar con getSystemSettings/updateSystemSettings v16.9.3

/**
 * Actualiza el peso específico de una comisión/instancia.
 */
export const updateCommissionWeight = async (commissionId: string, weight: number) => {
    await updateCommission(commissionId, { peso: weight });
};

/**
 * Aprueba un plan de trabajo (Nivel Vicepresidencia) con registro de auditoría.
 */
export const approveWorkPlan = async (planId: string, userId: string) => {
    await invokeGovMutation('manage-gov-planning-scoped', {
        action: 'approve_plan',
        plan_id: planId,
        changes: { status: 'APROBADO', approved_at: new Date().toISOString(), approved_by: userId }
    });
};

export const getDashboardByCommission = async (user: User): Promise<CommissionDashboard[]> => {
    let query = supabase.from('gov_v_commission_dashboard').select('*');

    // ─── SCOPE SHIELD v15.28.0 ─────────────────────────────────────────────────
    // Mismo criterio estricto que getWorkPlans: org_scope explícito prevalece.
    // ADMINISTRADOR es siempre GLOBAL. El nombre del usuario NO es criterio.
    const isAdminRole = user.role === 'SUPER_ADMIN' || user.role === 'ADMINISTRADOR' || user.role === 'DIRECTOR_NACIONAL';
    const scope = isAdminRole
        ? 'GLOBAL'
        : (user.org_scope || (
            user.role === 'COORDINADOR' ? 'COORDINATOR' :
            user.role === 'VICE_PRESIDENT' ? 'VP' :
            'INSTANCE'
        ));

    // ─── VP SCOPE: solo vicepresidency_id, nunca instance_id como substituto ─
    if (scope === 'VP') {
        const vpId = user.vicepresidency_id || null;
        if (vpId) {
            query = query.eq('vicepresidency_id', vpId);
        } else {
            // VP sin vicepresidency_id: no devuelve nada
            console.warn('[SCOPE_SHIELD] getDashboardByCommission: VP sin vicepresidency_id. Retornando vacío.');
            return [];
        }
    } else if (scope === 'INSTANCE' && (user.commission_id || user.instance_id)) {
        query = query.eq('commission_id', user.commission_id || user.instance_id);
    } else if (scope === 'COORDINATOR') {
        // Caso COORDINADOR: filtra por instancias específicas y vicepresidencias específicas
        const instIds = user.accessible_instances || [];
        const vpIds = user.accessible_vps || [];
        
        if (instIds.length > 0 && vpIds.length > 0) {
            query = query.or(`commission_id.in.(${instIds.join(',')}),vicepresidency_id.in.(${vpIds.join(',')})`);
        } else if (instIds.length > 0) {
            query = query.in('commission_id', instIds);
        } else if (vpIds.length > 0) {
            query = query.in('vicepresidency_id', vpIds);
        } else {
            // Sin acceso definido: no devuelve nada (seguridad)
            return [];
        }
    }

    const { data: viewData, error: viewError } = await query;
    if (viewError) throw viewError;

    return (viewData || []) as any[];
};

// ─── Talento ─────────────────────────────────────────────
/**
 * Actualiza un plan de trabajo
 */
export const updateWorkPlan = async (id: string, updates: any) => {
    console.log('[DB] updateWorkPlan called:', { id, updates });
    return invokeGovMutation('manage-gov-planning-scoped', {
        action: 'approve_plan',
        plan_id: id,
        changes: updates
    });
};

/** recalculate and update de-normalized totals in plan table */
export const syncWorkPlanTotals = async (planId: string) => {
    console.log('[DB] syncWorkPlanTotals called for plan:', planId);
    const planDetail = await getWorkPlanDetail(planId);

    // ═══ FIX v18.11.25: Sumar desde computeSectionMetrics (Contrato Canon) ═══
    // Seleccionamos 'name' para que resolveCanonicalSectionNumber pueda inferir el SN
    const sectionList = planDetail?.sections || [];
    let totalGoal = 0;
    let totalProgress = 0;

    sectionList.forEach(s => {
        // Resolvemos el número de sección de forma canónica (soporta SN null via inferencia de nombre)
        const sn = resolveCanonicalSectionNumber(s);
        
        if (sn && sn >= 1 && sn <= 9) {
            const persisted = Number(s.meta_anual ?? s.goal ?? 0);
            
            // v18.12.52: Usamos el nuevo motor con el objeto de sección completo
            const metrics = computeSectionMetrics({ 
                ...s, 
                section_number: sn, 
                goal: persisted 
            });
            
            // v18.12.52: S5 siempre dinámico, resto prioriza persistencia manual > 0
            const finalG = (sn === 5) ? metrics.goal : (persisted > 0 ? persisted : metrics.goal);
            
            totalGoal += finalG;
            totalProgress += metrics.progress;
            console.log(`[DB] Section S${sn} ("${s.name}") -> Goal: ${metrics.goal}, Progress: ${metrics.progress}`);
        } else {
            console.warn(`[DB] Section ID ${s.id} could not be resolved to a canonical number. Name: "${s.name}"`);
        }
    });
    
    console.log('[DB] syncWorkPlanTotals calculation (from sections):', { totalGoal, totalProgress, sectionCount: sectionList.length });

    // v18.12.16: Ya tenemos currentPlan cargado al inicio de la función
    // No es necesario volver a consultar, usamos el objeto ya disponible.
    
    const updatePayload: any = {
        total_progress: totalProgress,
        updated_at: new Date().toISOString()
    };

    // v18.12.51: Meta Global SIEMPRE es la suma dinámica de secciones.
    // Se elimina el hardlock para garantizar que el dashboard y el plan estén sincronizados.
    updatePayload.total_goal = totalGoal;
    console.log('[DB] syncWorkPlanTotals: Updating total_goal to sum of sections:', totalGoal);

    await invokeGovMutation('manage-gov-planning-scoped', {
        action: 'update_plan_totals',
        plan_id: planId,
        total_goal: updatePayload.total_goal,
        total_progress: updatePayload.total_progress
    });
    console.log('[DB] syncWorkPlanTotals SUCCESS');
};

/**
 * Borrado atómico de sección de plan de trabajo vía RPC (By-pass RLS)
 */
export const deleteWorkPlanSection = async (sectionId: string) => {
    console.log('[DB] deleteWorkPlanSection secure call for:', sectionId);
    await invokeGovMutation('manage-gov-planning-scoped', { action: 'delete_section', section_id: sectionId });
    return true;
};

/** 
 * SINCRONIZACIÓN DE ÍTEMS RELACIONALES (Shield v14.5.39)
 * Reconstruye gov_work_plan_items desde el JSON content de las secciones.
 * Esto garantiza que si se borra un elemento en la UI (JSON), desaparezca de la tabla relacional.
 */
export const syncWorkPlanItems = async (planId: string) => {
    console.log(`[DB] syncWorkPlanItems: Iniciando para plan ${planId}`);
    try {
        const planDetail = await getWorkPlanDetail(planId);
        const sections = planDetail?.sections || [];
        
        if (!sections.length) {
            console.error('[DB] syncWorkPlanItems error fetching sections:', 'No sections returned from secure detail endpoint');
            return;
        }

        const itemsToInsert: any[] = [];

        sections.forEach(section => {
            const content = section.content || {};
            const sn = Number(section.section_number);
            if (sn >= 1 && sn <= 9) {
                const authorId = 'SYSTEM'; // In a real scenario, this could be the user.
                const serialized = serializeSection(sn, content, planId, authorId);
                // Assign section_id and map correctly
                serialized.forEach((item, idx) => {
                    itemsToInsert.push({
                        work_plan_id: planId,
                        section_id: section.id,
                        type: `SEC_${sn}_ITEM`,
                        name: String(item.title).substring(0, 255),
                        description: String(item.description).substring(0, 500),
                        goal_value: 1, // Detailed goals handling will be updated eventually if needed, but the main calculation uses computeSectionMetrics on content anyway.
                        current_value: item.status === 'done' || item.metrics?.completado ? 1 : 0,
                        order_index: idx
                    });
                });
            }
        });

        await invokeGovMutation('manage-gov-planning-scoped', {
            action: 'sync_plan_items',
            plan_id: planId,
            items: itemsToInsert
        });
        console.log(`[DB] syncWorkPlanItems EXITO: ${itemsToInsert.length} ítems persistidos para plan ${planId}`);
    } catch (err: any) {
        console.error('[DB] syncWorkPlanItems FATAL ERROR:', err.message);
        throw err;
    }
};

export const getTalent = async (): Promise<TalentRecord[]> => {
    return fetchAllScopedGovUserRows<TalentRecord>('talent_directory', 'talents');
};

// Perfiles Directivos se manejan vía updateTalent

export const addTalent = async (
    input: { name: string; email?: string; role?: string; education?: string; experience?: string; interests?: string; areas?: string[]; experience_level?: number; projects?: string; sector?: string; other_expertise?: string; commission_id?: string | null },
    options: GovMutationOptions = {}
): Promise<string> => {
    const { role: cargo, sector, ...safeInput } = input;
    const response = await invokeGovMutation<{ talent: { id: string } }>('manage-gov-talent-scoped', {
        action: 'create_talent',
        talent: {
            ...safeInput,
            other_expertise: input.other_expertise ?? sector ?? cargo ?? ''
        }
    }, options);
    return response.talent.id;
};

export const updateTalent = async (id: string, updates: any, options: GovMutationOptions = {}) => {
    const { role: cargo, sector, id: _id, user_id: _userId, updated_at: _updatedAt, ...safeUpdates } = updates || {};
    const response = await invokeGovMutation<{ talent: TalentRecord }>('manage-gov-talent-scoped', {
        action: 'update_talent',
        talent_id: id,
        changes: {
            ...safeUpdates,
            ...(cargo !== undefined || sector !== undefined
                ? { other_expertise: safeUpdates.other_expertise ?? sector ?? cargo }
                : {})
        }
    }, options);
    return response.talent;
};

export const updateTalentExpertise = async (id: string, updates: any) => {
    return updateTalent(id, updates);
};

export const removeTalent = async (id: string, options: GovMutationOptions = {}) => {
    return invokeGovMutation<{ deleted_id: string }>('manage-gov-talent-scoped', {
        action: 'delete_talent',
        talent_id: id
    }, options);
};

// ─── Seguimiento ─────────────────────────────────────────
export const getTracking = async (user: User): Promise<MonthlyReport[]> => {
    const { data, error } = await supabase.from('cpx_monthly_reports')
        .select('*')
        .order('year', { ascending: false }).order('month', { ascending: false });
    if (error) throw error;

    const [commsRes, vpsRes] = await Promise.all([
        supabase.from('gov_commissions').select('id, name, vicepresidency_id'),
        supabase.from('gov_vicepresidencies').select('id, name')
    ]);

    if (commsRes.error) throw new Error(`Error al cargar catálogo de comisiones: ${commsRes.error.message}`);
    if (vpsRes.error) throw new Error(`Error al cargar catálogo de vicepresidencias: ${vpsRes.error.message}`);

    const commMap: Record<string, any> = {};
    (commsRes.data || []).forEach((c: any) => {
        commMap[c.id] = { id: c.id, name: c.name, vicepresidency_id: c.vicepresidency_id, entity_type: 'COMMISSION' };
    });

    const vpMap: Record<string, any> = {};
    (vpsRes.data || []).forEach((vp: any) => {
        vpMap[vp.id] = { id: vp.id, name: vp.name, vicepresidency_id: vp.id, entity_type: 'VICEPRESIDENCY' };
    });

    let foundDiscriminator = false;
    let unresolvedCount = 0;
    let collisions = 0;

    let result = (data || []).map((r: any) => {
        let resolved = null;
        const discriminator = r.entity_type || r.instance_type || r.report_type;
        
        if (discriminator) {
            foundDiscriminator = true;
            if (String(discriminator).toUpperCase().includes('COMMISSION') || String(discriminator).toUpperCase().includes('COMISION')) {
                resolved = commMap[r.commission_id];
            } else if (String(discriminator).toUpperCase().includes('VICEPRESIDENCY') || String(discriminator).toUpperCase().includes('VICEPRESIDENCIA')) {
                resolved = vpMap[r.commission_id];
            }
        }

        if (!resolved) {
            const inComm = commMap[r.commission_id];
            const inVp = vpMap[r.commission_id];
            
            if (inComm && inVp) {
                collisions++;
                if (import.meta.env.DEV) {
                    console.warn(`[getTracking] COLISIÓN DETECTADA: ID ${r.commission_id} en ambas tablas`);
                }
            }
            resolved = inComm || inVp || null;
        }

        if (!resolved) {
            unresolvedCount++;
            if (import.meta.env.DEV) {
                console.warn(`[getTracking] REPORTE SIN RESOLVER: commission_id ${r.commission_id}`);
            }
        }

        return {
            ...r,
            commission_name: resolved?.name ?? r.commission_name ?? '',
            gov_commissions: resolved
                ? {
                    name: resolved.name,
                    vicepresidency_id: resolved.vicepresidency_id,
                }
                : null,
        };
    });

    if (import.meta.env.DEV) {
        console.log(`[getTracking] Discriminador real encontrado: ${foundDiscriminator}`);
        if (collisions > 0) console.log(`[getTracking] Colisiones detectadas: ${collisions}`);
        if (unresolvedCount > 0) console.log(`[getTracking] Reportes sin resolver: ${unresolvedCount}`);
    }

    const scope = user.org_scope || (user.role === 'SUPER_ADMIN' || user.role === 'ADMINISTRADOR' ? 'GLOBAL' : user.role === 'COORDINADOR' ? 'COORDINATOR' : user.role === 'VICE_PRESIDENT' ? 'VP' : 'INSTANCE');
    const isFullView = scope === 'GLOBAL';

    if (!isFullView) {
        if (scope === 'VP' && (user.instance_id || user.vicepresidency_id)) {
            const vpId = user.instance_id || user.vicepresidency_id;
            result = result.filter((r: any) => r.gov_commissions?.vicepresidency_id === vpId);
        } else if (scope === 'INSTANCE' && (user.instance_id || user.commission_id)) {
            const instId = user.instance_id || user.commission_id;
            result = result.filter(r => r.commission_id === instId);
        } else if (scope === 'COORDINATOR') {
            const instIds = user.accessible_instances || [];
            const vpIds = user.accessible_vps || [];
            result = result.filter((r: any) => 
                instIds.includes(r.commission_id) || 
                (r.gov_commissions?.vicepresidency_id && vpIds.includes(r.gov_commissions.vicepresidency_id))
            );
        } else if (user.role === 'VICE_PRESIDENT' && user.vicepresidency_id) {
            result = result.filter((r: any) => r.gov_commissions?.vicepresidency_id === user.vicepresidency_id);
        } else if (user.role === 'COMMISSION_PRESIDENT' && user.commission_id) {
            result = result.filter(r => r.commission_id === user.commission_id);
        }
    }


    return result;
};

export const addTracking = async (commissionId: string, data: { month: number; year: number; status?: string }): Promise<string> => {
    const response = await invokeGovSecureFunction('manage-gov-direct-writes-scoped', {
        action: 'report_create', instance_id: commissionId, report: data,
    });
    return response.report.id;
};

export const updateTracking = async (id: string, updates: Partial<MonthlyReport>) => {
    await invokeGovSecureFunction('manage-gov-direct-writes-scoped', { action: 'report_update', report_id: id, changes: updates });
};

export const removeTracking = async (id: string) => {
    await invokeGovSecureFunction('manage-gov-direct-writes-scoped', { action: 'report_delete', report_id: id });
};

// ─── Memoria ─────────────────────────────────────────────
const memoryApi = <T extends Record<string, any>>(body: Record<string, any>): Promise<T> =>
    invokeGovSecureFunction<T>('manage-gov-memory-scoped', body);

export const getMemory = async (_user: User): Promise<DocRecord[]> => {
    const rows: DocRecord[] = [];
    for (let offset = 0; ; offset += 100) {
        const response = await memoryApi<{ documents: DocRecord[] }>({ action: 'list', status: 'ALL', offset, limit: 100 });
        rows.push(...response.documents);
        if (response.documents.length < 100) return rows;
    }
};

export const readMemory = async (id: string): Promise<DocRecord> =>
    (await memoryApi<{ document: DocRecord }>({ action: 'read', document_id: id })).document;

export const addMemory = async (commissionId: string, input: { title: string; type: string; url?: string; content?: any; category?: string; upload_id?: string; source_type?: 'FILE' | 'EXTERNAL_LINK' | 'INTERNAL' }, options: GovMutationOptions = {}): Promise<string> => {
    const source = input.source_type || (input.upload_id ? 'FILE' : input.url === '#' ? 'INTERNAL' : 'EXTERNAL_LINK');
    const result = await memoryApi<{ document: DocRecord }>({
        action: 'create', idempotency_key: options.idempotencyKey || createMutationIdempotencyKey(),
        fields: { ...input, commission_id: commissionId, category: input.category || 'DOCUMENTO', source_type: source }
    });
    return result.document.id;
};

// Upload receives an authorized institutional owner, never a client-chosen object path.
export const uploadFile = async (file: File, commissionId: string, options: GovMutationOptions = {}): Promise<string> => {
    const reservation = await memoryApi<{ upload_id: string; path: string; token: string }>({
        action: 'prepare_upload', idempotency_key: options.idempotencyKey || createMutationIdempotencyKey(),
        fields: { commission_id: commissionId, file_name: file.name, file_size: file.size, mime_type: file.type || 'application/octet-stream' }
    });
    const { error } = await supabase.storage.from('gov_memory').uploadToSignedUrl(reservation.path, reservation.token, file, { contentType: file.type || 'application/octet-stream', upsert: false });
    if (error) throw error;
    return reservation.upload_id;
};

export type DocumentAccess = { url?: string; mime_type?: string; ttl?: number; source_type?: string; content?: any };
export const requestDocumentAccess = (documentId: string): Promise<DocumentAccess> =>
    memoryApi<DocumentAccess>({ action: 'content', document_id: documentId });
export const getDocumentAccessUrl = async (documentId: string): Promise<string> => {
    const result = await requestDocumentAccess(documentId);
    if (!result.url) throw new Error('Este documento contiene un reporte interno.');
    return result.url;
};
export const updateMemory = async (id: string, fields: Pick<DocRecord, 'title' | 'type'> & { category?: string }, expectedRevision: number, options: GovMutationOptions = {}) =>
    memoryApi<{ document: DocRecord }>({ action: 'edit', document_id: id, fields, expected_revision: expectedRevision, idempotency_key: options.idempotencyKey || createMutationIdempotencyKey() });
export const archiveMemory = (doc: DocRecord, options: GovMutationOptions = {}) =>
    memoryApi<{ document: DocRecord }>({ action: 'archive', document_id: doc.id, expected_revision: doc.revision, idempotency_key: options.idempotencyKey || createMutationIdempotencyKey() });
export const restoreMemory = (doc: DocRecord, options: GovMutationOptions = {}) =>
    memoryApi<{ document: DocRecord }>({ action: 'restore', document_id: doc.id, expected_revision: doc.revision, idempotency_key: options.idempotencyKey || createMutationIdempotencyKey() });
// Compatibility name now archives; physical deletion is unavailable.
export const removeMemory = (id: string, expectedRevision: number) => archiveMemory({ id, revision: expectedRevision } as DocRecord);
export const readMemoryAcl = (id: string) => memoryApi<{ acl_mode: 'INHERIT_SCOPE' | 'RESTRICTED'; user_ids: string[]; revision: number }>({ action: 'read_acl', document_id: id });
export const updateMemoryAcl = (id: string, expectedRevision: number, aclMode: string, userIds: string[], options: GovMutationOptions = {}) =>
    memoryApi<{ document: DocRecord }>({ action: 'update_acl', document_id: id, expected_revision: expectedRevision, acl_mode: aclMode, user_ids: userIds, idempotency_key: options.idempotencyKey || createMutationIdempotencyKey() });

// ─── Configuraciones del Sistema ──────────────────────────
import { SystemSettings } from './shared-logic';

const MASTER_DEFAULT_SETTINGS: SystemSettings = {
    threshold_green: 90,
    threshold_amber: 75,
    weight_comisiones: 50,
    weight_comites: 30,
    weight_consejerias: 20,
    weight_dimension_plans: 30,
    weight_dimension_kpis: 70,
    plan_weight: 30,
    kpi_weight: 70
};

export const getSystemSettings = async (): Promise<SystemSettings> => {
    const { data, error } = await supabase.from('gov_settings').select('*').single();
    if (error || !data) {
        console.warn("GOV_SETTINGS: Usando Master Defaults por falta de registro en DB");
        return MASTER_DEFAULT_SETTINGS;
    }
    
    return {
        ...MASTER_DEFAULT_SETTINGS,
        ...data,
        updated_at: data.updated_at
    };
};

export const updateSystemSettings = async (settings: Partial<SystemSettings>) => {
    // 1. Validaciones de Negocio Obligatorias (Regla #CFG-001)
    
    // Validación Dimensión (Plan vs KPIs)
    if (settings.weight_dimension_kpis !== undefined && settings.weight_dimension_plans !== undefined) {
        if (settings.weight_dimension_kpis + settings.weight_dimension_plans !== 100) {
            throw new Error("LA SUMA DE PESOS (KPI + PLAN) DEBE SER 100%");
        }
    }

    // Validación Categorías (Instancias)
    if (settings.weight_comisiones !== undefined && settings.weight_comites !== undefined && settings.weight_consejerias !== undefined) {
        const totalCat = settings.weight_comisiones + settings.weight_comites + settings.weight_consejerias;
        if (totalCat !== 100) {
            throw new Error(`LA SUMA DE CATEGORÍAS DEBE SER 100% (ACTUAL: ${totalCat}%)`);
        }
    }

    // 2. Persistencia (Solo columnas reales de gov_settings)
    const { error } = await supabase.from('gov_settings').upsert({
        id: 1, 
        threshold_green: settings.threshold_green,
        threshold_amber: settings.threshold_amber,
        weight_comisiones: settings.weight_comisiones,
        weight_comites: settings.weight_comites,
        weight_consejerias: settings.weight_consejerias,
        weight_dimension_plans: settings.weight_dimension_plans,
        weight_dimension_kpis: settings.weight_dimension_kpis,
        updated_at: new Date().toISOString()
    });

    if (error) {
        console.error("SUPABASE_SAVE_ERROR:", error);
        throw new Error(`Error en base de datos: ${error.message}`);
    }
};


// ─── Free AI Engine Helpers ──────────────────────────────
export const getAIUsage = async (userId: string) => {
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
        .from('gov_ai_usage')
        .select('count')
        .eq('user_id', userId)
        .eq('usage_date', today)
        .maybeSingle();
    
    if (error && error.code !== 'PGRST116') throw error;
    return data?.count || 0;
};

export const signUp = async (_email: string, _password: string, _name: string): Promise<never> => {
    throw new GovScopedReadError('El alta publica de usuarios esta deshabilitada', 403);
};

// ─── Gestión de Usuarios y Permisos ───────────────────────
/**
 * Busca un usuario existente por email (case-insensitive, trimmed).
 * Devuelve el usuario completo o null si no existe.
 */
export const createUser = async (userData: { 
    name: string; 
    email: string; 
    role: UserRole; 
    password?: string; 
    vicepresidency_id?: string | null; 
    commission_id?: string | null; 
    accessible_modules?: string[] | null; 
    org_scope?: string;
    accessible_instances?: string[];
    accessible_vps?: string[];
}, options: GovMutationOptions = {}) => {
    const response = await invokeGovMutation<{ user: User }>('manage-gov-users-scoped', {
        action: 'create_user',
        user: userData
    }, options);
    return requireConfirmedUser(response.user).id;
};

const pickUserMutationFields = (updates: Partial<User>): Record<string, unknown> => {
    const allowed = [
        'name', 'email', 'role', 'password', 'org_scope', 'vicepresidency_id',
        'commission_id', 'accessible_modules', 'accessible_instances', 'accessible_vps'
    ] as const;
    return Object.fromEntries(allowed
        .filter((key) => Object.prototype.hasOwnProperty.call(updates, key))
        .map((key) => [key, updates[key]]));
};

const requireConfirmedUser = (value: unknown, expectedId?: string): User => {
    if (!value || typeof value !== 'object') {
        throw new GovScopedReadError('Invalid mutation response');
    }
    const user = value as User;
    if (typeof user.id !== 'string' || !user.id || (expectedId && user.id !== expectedId)) {
        throw new GovScopedReadError('Invalid mutation response');
    }
    return user;
};

export const updateUser = async (id: string, updates: Partial<User>, options: GovMutationOptions = {}) => {
    const changes = pickUserMutationFields(updates);
    const action = Object.keys(changes).length === 1 && typeof changes.password === 'string'
        ? 'change_password'
        : 'update_user';
    const response = await invokeGovMutation<{ user: User }>('manage-gov-users-scoped', {
        action,
        user_id: id,
        changes
    }, options);
    return requireConfirmedUser(response.user, id);
};

export const changeUserPassword = async (id: string, password: string, options: GovMutationOptions = {}) => {
    return updateUser(id, { password }, options);
};

export const deleteUser = async (id: string, options: GovMutationOptions = {}) => {
    const response = await invokeGovMutation<{ deleted_id: string }>('manage-gov-users-scoped', {
        action: 'delete_user',
        user_id: id
    }, options);
    if (!isConfirmedGovUserDeletion(response, id)) {
        throw new GovScopedReadError('Invalid mutation response');
    }
    return response.deleted_id;
};

// ─── Sincronización de Planes de Trabajo ──────────────────
// COLUMNAS REALES VERIFICADAS (audit 2026-04-08):
// gov_work_plans: id, commission_id, year, status, created_at, updated_at, vp_approved, indicators_president_approved, replicated_from, total_progress
// gov_work_plan_sections: id, work_plan_id, name, order_index, meta_anual, created_at, updated_at, content
// gov_work_plan_items: id, work_plan_id, section_id, ...
// gov_capture_indicators: id, instance_id, commission_id, name, unit, frequency, target_anual, metaanual, weight, history, compliance_rate, last_value, orden, logic_config

/**
 * FUSIÓN SEGURA NO DESTRUCTIVA (v18.21.23 - SAFE MERGE)
 * 
 * Mezcla la configuración del origen maestro con los datos capturados y
 * configuraciones del destino local sin pérdida de datos históricos.
 */
export const safeMergeSectionContent = (
    sourceContent: any,
    destinationContent: any
): any => {
    if (!destinationContent) return sourceContent || {};
    if (!sourceContent) return destinationContent || {};

    const merged = { ...destinationContent };

    // Claves críticas de avance real e historial que jamás deben ser destruidas
    const PROTECTED_KEYS = [
        'real', 'valor', 'avance', 'progress', 'compliance', 'history_log',
        'last_value', 'validado', 'approved', 'comentario', 'comentarios',
        'comment', 'comments', 'note', 'nota', 'observacion', 'observaciones'
    ];

    Object.keys(sourceContent).forEach(key => {
        const kl = key.toLowerCase();
        const isProtected = PROTECTED_KEYS.some(pk => kl.includes(pk));

        // Regla 3, 4 y 5: Mezcla segura de configuraciones estructurales y banderas
        if (key.startsWith('_config_') || key === 'columns' || key.includes('columns_')) {
            // Regla 5: Fusionar columnas dinámicas de arrays sin duplicados
            if (Array.isArray(sourceContent[key]) && Array.isArray(destinationContent[key])) {
                const mergedArray = [...destinationContent[key]];
                sourceContent[key].forEach((srcItem: any) => {
                    const exists = mergedArray.some((dstItem: any) => 
                        (srcItem?.name && dstItem?.name && String(srcItem.name).trim().toLowerCase() === String(dstItem.name).trim().toLowerCase()) ||
                        (srcItem?.id && dstItem?.id && srcItem.id === dstItem.id) ||
                        (srcItem?.accessor && dstItem?.accessor && srcItem.accessor === dstItem.accessor)
                    );
                    if (!exists) {
                        mergedArray.push(srcItem);
                    }
                });
                merged[key] = mergedArray;
            } else {
                // Preservar la configuración local si ya fue definida de forma explícita
                if (destinationContent[key] !== undefined) {
                    // Reportar potencial conflicto si los valores son diferentes y no vacíos
                    if (JSON.stringify(sourceContent[key]) !== JSON.stringify(destinationContent[key])) {
                        if (!merged._conflict_warnings) {
                            merged._conflict_warnings = [];
                        }
                        merged._conflict_warnings.push(`Conflicto de configuración en clave [${key}]. Se preservó el valor del destino local.`);
                    }
                    merged[key] = destinationContent[key];
                } else {
                    merged[key] = sourceContent[key];
                }
            }
            return;
        }

        // Regla 1: Preservar captura y datos de avance real
        if (isProtected) {
            const hasDestValue = destinationContent[key] !== undefined && destinationContent[key] !== 0 && destinationContent[key] !== "";
            merged[key] = hasDestValue ? destinationContent[key] : sourceContent[key];
            return;
        }

        // Regla 6 y 7: Objetos anidados recursivos y arrays
        if (typeof sourceContent[key] === 'object' && sourceContent[key] !== null) {
            if (Array.isArray(sourceContent[key])) {
                // Si es un array de captura, no reemplazar el del destino
                if (destinationContent[key] !== undefined && Array.isArray(destinationContent[key])) {
                    merged[key] = destinationContent[key];
                } else {
                    merged[key] = sourceContent[key];
                }
            } else if (typeof destinationContent[key] === 'object' && destinationContent[key] !== null) {
                merged[key] = safeMergeSectionContent(sourceContent[key], destinationContent[key]);
            } else {
                merged[key] = sourceContent[key];
            }
        } else {
            // Regla 6: Cargar llaves de configuración nuevas del origen
            if (destinationContent[key] === undefined) {
                merged[key] = sourceContent[key];
            } else {
                // Si el destino ya tiene valor, preservarlo
                merged[key] = destinationContent[key];
            }
        }
    });

    return merged;
};

/**
 * REPLICA MASTER v2026.2 — Motor Espejo Total
 * 
 * @param syncMode 'ESTRUCTURA' | 'ESTRUCTURA_Y_METAS'
 *   - ESTRUCTURA:       Clona SOLO la estructura (secciones, ítems, formatos). Las metas quedan en 0.
 *   - ESTRUCTURA_Y_METAS: Clona estructura + metas (meta_anual de secciones + target_anual de indicadores).
 * 
 * SIEMPRE ejecuta Tabula Rasa (limpieza nuclear) antes de clonar.
 */
export const replicateWorkPlan = async (
    sourcePlanId: string,
    targetInstanceIds: string[],
    options: { 
        syncStructure: boolean;   // legacy compat, siempre true
        syncMetas: boolean;        // legacy: equivale a syncMode === 'ESTRUCTURA_Y_METAS'
        syncMode?: 'ESTRUCTURA' | 'ESTRUCTURA_Y_METAS';
    }
): Promise<{ ok: number; error: number; errors: string[] }> => {
    // GUARDIA DE FLUJO DESTRUCTOR: BLOCK_SYNC ACTIVO (v18.21.24)
    // Impide cualquier ejecuci??n real de replicateWorkPlan para proteger los datos en producci??n.
    console.error("[BLOCK_SYNC] Intento de sincronizaci??n destructiva bloqueada por software.");
    throw new Error("BLOCK_SYNC_ACTIVE: La sincronizaci??n destructiva en caliente de planes maestros est?? terminantemente bloqueada en esta versi??n.");
};

/**
 * SINCRONIZACIÓN SEGURA NO DESTRUCTIVA (v18.21.25 - SAFE MERGE FLOW)
 * 
 * Replica la estructura y configuraciones maestras del plan de origen
 * hacia las comisiones receptoras fusionando y protegiendo los datos locales
 * de captura y banderas de visibilidad locales sin borrar secciones destino.
 */
export const replicateWorkPlanSafeMerge = async (
    sourcePlanId: string,
    targetInstanceIds: string[],
    options: {
        syncStructure: boolean;
        syncMetas: boolean;
        syncMode?: 'ESTRUCTURA' | 'ESTRUCTURA_Y_METAS';
        runDryRunOnly?: boolean; // Control inofensivo preventivo
    }
) => {
    // BLINDAJE PREVENTIVO GLOBAL (Fase de integración segura)
    // Impide escrituras accidentales en Supabase en caliente
    const isDryRunMode = options.runDryRunOnly !== false; // Por defecto siempre true en esta fase de integración
    
    console.log(`[SAFE-SYNC] 🛡️ Iniciando flujo seguro Safe Merge. Dry-Run: ${isDryRunMode}`);
    const results = { ok: 0, error: 0, warningsCount: 0, errors: [] as string[] };
    const includeMetas = options.syncMode === 'ESTRUCTURA_Y_METAS' || options.syncMetas === true;

    try {
        // ?????? FASE 1: LEER PLAN MAESTRO ORIGEN ??????
        const sourcePlan = await fetchScopedWorkPlanDetailRaw(sourcePlanId, { includeItems: true });
        if (!sourcePlan) throw new Error(`[SAFE-SYNC] Plan origen no encontrado: ${sourcePlanId}`);

        const sourceSections = sourcePlan.sections || [];
        if (!sourceSections.length) {
            throw new Error(`[SAFE-SYNC] Error obteniendo secciones origen: Plan sin secciones`);
        }

        const sourceItems = sourcePlan.items || [];

        // ?????? FASE 2: ITERAR DESTINOS ??????
        // ── FASE 2: ITERAR DESTINOS ──────────────────────────────────────────
        for (const targetId of targetInstanceIds) {
            try {
                // Obtener Plan Destino
                const resolvedTargetPlans = await resolvePointWorkPlans([targetId], sourcePlan.year);
                const targetPlanRef = resolvedTargetPlans[targetId];
                let targetPlanId = targetPlanRef?.plan_id;

                if (!targetPlanId) {
                    if (isDryRunMode) {
                        console.log(`[DRY-RUN] Se crear??a plan en borrador para la comisi??n: ${targetId}`);
                    } else {
                        // Creaci??n segura no destructiva del plan destino
                        const seeded = await invokeGovSecureFunction('manage-gov-direct-writes-scoped', {
                            action: 'seed_replicated_plan', source_plan_id: sourcePlanId, instance_id: targetId,
                        });
                        targetPlanId = seeded.plan_id;
                    }
                }

                // Obtener secciones destino actuales (para mergear)
                let targetSections: any[] = [];
                if (targetPlanId) {
                    const targetPlanDetail = await fetchScopedWorkPlanDetailRaw(targetPlanId, { includeItems: true });
                    targetSections = targetPlanDetail?.sections || [];
                }

                console.log(`[SAFE-SYNC] Procesando comisión: ${targetId} | Secciones destino: ${targetSections.length}`);

                // Fusión y mezcla de secciones (NO Tabula Rasa)
                const sectionsToUpdate: any[] = [];
                const sectionsToInsert: any[] = [];
                const orphanDestinationSections = targetSections.filter((ts, tIdx) => {
                    const destinationPosition = Number(ts.order_index ?? ts.section_number ?? (tIdx + 1));
                    return !sourceSections.some((_, srcIdx) => srcIdx === destinationPosition - 1);
                });

                sourceSections.forEach((srcSec, idx) => {
                    // Emparejar por posición de orden index (físico)
                    const tgtSec = targetSections.find((ts, tIdx) => tIdx === idx);

                    if (tgtSec) {
                        // Sección ya existe -> MEZCLA SEGURA (Safe Merge)
                        const mergedContent = safeMergeSectionContent(srcSec.content, tgtSec.content);
                        if (mergedContent._conflict_warnings) {
                            results.warningsCount += mergedContent._conflict_warnings.length;
                        }

                        sectionsToUpdate.push({
                            id: tgtSec.id,
                            name: srcSec.name,
                            meta_anual: includeMetas ? (Number(srcSec.meta_anual) || 0) : tgtSec.meta_anual,
                            goal: includeMetas ? (Number(srcSec.goal) || 0) : tgtSec.goal,
                            content: mergedContent
                        });
                    } else {
                        // Sección nueva en origen -> AGREGAR
                        sectionsToInsert.push({
                            work_plan_id: targetPlanId || 'temp-id',
                            name: srcSec.name,
                            order_index: idx + 1,
                            meta_anual: includeMetas ? (Number(srcSec.meta_anual) || 0) : 0,
                            goal: includeMetas ? (Number(srcSec.goal) || 0) : 0,
                            progress: 0,
                            content: srcSec.content
                        });
                    }
                });

                // Reporte visual de acciones en Dry-Run
                if (isDryRunMode) {
                    console.log(`[DRY-RUN REPORT] Comisión ${targetId}:`);
                    console.log(`   - Secciones a mezclar con Safe Merge: ${sectionsToUpdate.length}`);
                    console.log(`   - Secciones nuevas a insertar:      ${sectionsToInsert.length}`);
                    console.log(`   - Secciones huerfanas destino a preservar: ${orphanDestinationSections.length} (PRESERVE_DESTINATION_ORPHAN_SECTION)`);
                    console.log(`   - Secciones custom locales a mantener: ${targetSections.filter(ts => ts.content?._config_custom_section === true).length}`);
                    console.log(`   - Cero llamadas a nuclear_delete_plan_sections.`);
                    console.log(`   - Cero escrituras ejecutadas en la base de datos.`);
                } else {
                    // FLUJO REAL BLOQUEADO PREVENTIVAMENTE
                    throw new Error("SAFE_SYNC_INTEGRATION_ACTIVE: La escritura física del Safe Merge está deshabilitada en esta fase de integración. Utilice el parámetro runDryRunOnly = true.");
                }

                results.ok++;
            } catch (err: any) {
                console.error(`[SAFE-SYNC] Error en destino ${targetId}:`, err.message);
                results.error++;
                results.errors.push(`${targetId}: ${err.message}`);
            }
        }

        return results;
    } catch (globalErr: any) {
        console.error('[SAFE-SYNC] ERROR GLOBAL:', globalErr);
        throw globalErr;
    }
};



/**
 * Reordena las secciones de un plan de trabajo (drag & drop)
 */
export const reorderSections = async (planId: string, orderedSectionIds: string[]) => {
    console.log('[REORDER] Iniciando reorden para plan:', planId);
    await invokeGovMutation('manage-gov-planning-scoped', {
        action: 'reorder_sections',
        plan_id: planId,
        section_ids: orderedSectionIds
    });
    console.log('[REORDER] Reorden exitoso.');
};

export const addWorkPlanSection = async (planId: string, section: Record<string, any>) => {
    return invokeGovMutation('manage-gov-planning-scoped', {
        action: 'add_section',
        plan_id: planId,
        section
    });
};

// ─── Seguimiento e Indicadores (v3.0) ────────────────────
export interface Indicator {
    id: string;
    instance_id: string;
    nombre: string;
    unidad: string;
    tipo: string;
    calculo: string;
    frec: string;
    metaAnual: number;
    peso: number;
    history: any[];
    compliance_rate?: number;
    comentarioGlobal?: string;
    orden?: number;
    protocolType?: string;        // 'PAI' | 'MITIGACIÓN' | 'MANTENIMIENTO'
    protocolActions?: any[];      // [{accion, fecha, notas, impacto}]
}

export type IndicatorSyncSafeMode =
    | 'CATALOG_ONLY_KEEP_DESTINATION_DATA'
    | 'CATALOG_AND_ORDER_KEEP_DESTINATION_DATA'
    | 'CATALOG_ORDER_AND_TARGETS_KEEP_PROGRESS'
    | 'FULL_STRUCTURE_REPLACE_EXPLICIT';

const INDICATOR_SYNC_SAFE_MODES: IndicatorSyncSafeMode[] = [
    'CATALOG_ONLY_KEEP_DESTINATION_DATA',
    'CATALOG_AND_ORDER_KEEP_DESTINATION_DATA',
    'CATALOG_ORDER_AND_TARGETS_KEEP_PROGRESS'
];

const TRACKING_PERIODS = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];

export function generateIndicatorKey(name: string): string {
    const normName = normalize(name || '');
    if (
        normName === 'REPORTES DE TEMAS DE AGENDA COMUN' || 
        normName === 'REPORTES DE TEMAS DEAGENDA COMUN' || 
        normName === 'REPORTES TEMAS DEAGENDA COMUN' || 
        normName === 'TEMAS DE AGENDA COMUN DE SOCIOS DIRECTOS' || 
        normName === 'TEMAS DE AGENDA COMUN CON SOCIOS DIRECTOS'
    ) {
        return 'TEMAS_AGENDA_COMUN';
    }
    return normName.replace(/[^A-Z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
}

export function getIndicatorKey(indicator: any): string {
    if (!indicator) return '';
    
    // a) indicator.indicator_key
    if (indicator.indicator_key && typeof indicator.indicator_key === 'string' && indicator.indicator_key.trim()) {
        return indicator.indicator_key.trim().toUpperCase();
    }
    
    // b) indicator.canonical_key
    if (indicator.canonical_key && typeof indicator.canonical_key === 'string' && indicator.canonical_key.trim()) {
        return indicator.canonical_key.trim().toUpperCase();
    }
    
    // c) indicator.logic_config?.indicator_key
    const logicConfig = indicator.logic_config;
    if (logicConfig && typeof logicConfig === 'object') {
        const lk = logicConfig.indicator_key || logicConfig.canonical_key;
        if (lk && typeof lk === 'string' && lk.trim()) {
            return lk.trim().toUpperCase();
        }
    }
    
    // Fallback final: generar clave estable desde nombre
    return generateIndicatorKey(indicator.nombre || indicator.name);
}

const normalizeIndicatorName = (value: any): string => normalize(String(value || ''));

const syncHistoryKeepDestinationProgress = (destinationHistory: any[] = [], sourceHistory: any[] = [], copyTargets: boolean) => {
    const destination = Array.isArray(destinationHistory) ? destinationHistory : [];
    const source = Array.isArray(sourceHistory) ? sourceHistory : [];

    return TRACKING_PERIODS.map((period) => {
        const current = destination.find((h: any) => normalize(h?.period || h?.month) === normalize(period)) || {};
        const sourcePeriod = source.find((h: any) => normalize(h?.period || h?.month) === normalize(period)) || {};

        return {
            ...current,
            period,
            meta: copyTargets ? Number(sourcePeriod?.meta ?? sourcePeriod?.target ?? 0) : Number(current?.meta ?? current?.target ?? 0),
            real: Number(current?.real ?? current?.valor ?? current?.value ?? 0),
            note: current?.note ?? current?.comentarios ?? current?.notes ?? current?.comment ?? '',
            elements: Array.isArray(current?.elements) ? current.elements : [],
            activities: Array.isArray(current?.activities) ? current.activities : [],
            checklist: Array.isArray(current?.checklist) ? current.checklist : []
        };
    });
};

const buildNewIndicatorHistoryFromSource = (sourceHistory: any[] = [], copyTargets: boolean) => {
    const source = Array.isArray(sourceHistory) ? sourceHistory : [];
    return TRACKING_PERIODS.map((period) => {
        const sourcePeriod = source.find((h: any) => normalize(h?.period || h?.month) === normalize(period)) || {};
        return {
            period,
            meta: copyTargets ? Number(sourcePeriod?.meta ?? sourcePeriod?.target ?? 0) : 0,
            real: 0,
            note: '',
            elements: [],
            activities: [],
            checklist: []
        };
    });
};

export const getIndicatorsLegacy = async (instanceIds: string | string[]): Promise<Indicator[]> => {
    return getIndicators(instanceIds);
};

/**
 * Helper interno para normalizar la estructura de indicadores desde cualquier fuente pública.
 */
export const getIndicators = async (instanceIds: string | string[]): Promise<Indicator[]> => {
    if (!instanceIds || (Array.isArray(instanceIds) && instanceIds.length === 0)) return [];
    const idsArray = (Array.isArray(instanceIds) ? instanceIds : [instanceIds]).map(id => String(id).trim());
    const indicators = await fetchScopedIndicatorsRaw({ instanceIds: idsArray });
    return normalizeIndicatorList(indicators);
};

function normalizeIndicatorList(raw: any[]): Indicator[] {
    const months = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
    
    return raw.map((ind: any) => {
        const config = ind.logic_config || {};
        const metaValue = Number(ind.metaanual || ind.metaAnual || ind.target_anual || 0);
        
        // Normalización Robusta del Historial (v18.7.9)
        const rawHistory = Array.isArray(ind.history) ? ind.history : [];
        const normalizedHistory = months.map(m => {
            const found = rawHistory.find((h: any) => 
                (h.period || h.month || '').toString().toUpperCase().startsWith(m)
            );
            return {
                period: m,
                meta: found ? Number(found.meta || 0) : 0,
                real: found ? Number(found.real || 0) : 0,
                note: found ? (found.note || found.comment || '') : '',
                checklist: found ? (found.checklist || []) : [],
                elements: found ? (found.elements || []) : [],
                activities: found ? (found.activities || []) : [],
                ...(typeof found?.captureConfirmedAt === 'string' && Number.isFinite(Date.parse(found.captureConfirmedAt))
                    ? { captureConfirmedAt: found.captureConfirmedAt }
                    : {})
            };
        });

        return {
            ...ind,
            id: String(ind.id).trim(),
            instance_id: String(ind.instance_id || ind.unified_instance_id || ind.commission_id).trim(),
            nombre: ind.nombre || ind.name || 'Indicador',
            unidad: ind.unidad || ind.unit || '#',
            frec: ind.frec || ind.frequency || 'Mensual',
            metaanual: metaValue,
            metaAnual: metaValue,
            peso: Number(ind.peso ?? ind.weight ?? 0),
            history: normalizedHistory,
            orden: Number(ind.orden ?? 0),
            compliance_rate: Number(ind.compliance_rate || 0),
            comentarioGlobal: ind.comentario_global || ind.comentarioGlobal || ind.comentarioglobal || '',
            protocolType: ind.protocol_type || ind.protocolType || config.protocol_type || '',
            protocolActions: ind.protocol_actions || ind.protocolActions || config.protocol_actions || [],
            tipo: ind.tipo || config.tipo || 'Maximizar',
            calculo: ind.calculo || config.calculo || 'Sumar',
            modo: (ind.modo === 'checklist' || config.modo === 'checklist') ? 'checklist' : 'normal'
        };
    }).sort((a: any, b: any) => (a.orden || 0) - (b.orden || 0));
}


/**
 * Guarda y/o actualiza de forma segura la lista de indicadores para una instancia específica.
 * v16.7.10: PURGA SEGURA — Solo elimina registros vacíos sin historial real.
 * NUNCA elimina indicadores con datos históricos por ausencia en payload.
 * @param {string} instanceId - El UUID de la instancia dueña de los indicadores.
 * @param {any[]} indicators - Array del estado actual de indicadores de la interfaz con datos frescos.
 * @returns {Promise<void>} 
 */
export const saveIndicators = async (instanceId: string, indicators: any[] = [], options: { idempotencyKey?: string } = {}) => {
    if (!Array.isArray(indicators)) {
        throw new Error('Los indicadores proporcionados no son un arreglo válido');
    }

    const DEBUG_PERSIST = typeof window !== 'undefined' && localStorage.getItem('GOV_AUDIT') === 'true';

    if (DEBUG_PERSIST) {
        console.group(`[PERSIST:SAVE] instanceId=${instanceId}, payload=${indicators.length} indicadores`);
    }

    // --- BLINDAJE DE PROPIETARIOS (INDICATOR OWNER IMMUTABILITY SHIELD v18.21.18) ---
    // 1. Extraer los IDs reales del payload
    const incomingIds = indicators
        .map(i => i.id || '')
        .filter(id => id && !id.startsWith('temp-'));

    const dbIndicatorsMap = new Map<string, any>();
    if (incomingIds.length > 0) {
        try {
            const dbIndicators = await fetchScopedIndicatorsRaw({ indicatorIds: incomingIds });
            dbIndicators.forEach(d => dbIndicatorsMap.set(d.id, d));
        } catch (fetchError: any) {
            console.error("[IMMUTABILITY_SHIELD] Error consultando indicadores existentes por ID:", fetchError);
            throw new Error(`[IMMUTABILITY_SHIELD] Error al validar inmutabilidad de propietario: ${fetchError.message}`);
        }
    }

    // 2. Validar que no se intente cambiar de dueño (inmutabilidad física de instance_id/commission_id)
    for (const ind of indicators) {
        const id = ind.id;
        if (id && !id.startsWith('temp-') && dbIndicatorsMap.has(id)) {
            const dbRecord = dbIndicatorsMap.get(id);
            const dbInstanceId = String(dbRecord.instance_id || '').trim();
            const dbCommissionId = String(dbRecord.commission_id || '').trim();
            const targetInstanceId = String(instanceId || '').trim();

            const isInstanceMismatch = dbInstanceId && dbInstanceId !== targetInstanceId;
            const isCommissionMismatch = dbCommissionId && dbCommissionId !== targetInstanceId;

            if (isInstanceMismatch || isCommissionMismatch) {
                const errorMsg = `[IMMUTABILITY_SHIELD] Guardado bloqueado: Intento ilegal de cambiar el propietario del indicador preexistente ${id}. Propietario actual en DB: [instance_id: ${dbInstanceId}, commission_id: ${dbCommissionId}]. Propietario intentado: [instance_id: ${targetInstanceId}, commission_id: ${targetInstanceId}].`;
                console.error(errorMsg);
                throw new Error(errorMsg);
            }
        }
    }

    // 1. Obtener los IDs actuales de la tabla maestra para esta instancia activa (para detectar eliminados ausentes)
    const govInds = await fetchScopedIndicatorsRaw({
        instanceIds: [instanceId]
    });

    const currentGovMap = new Map<string, any>();
    (govInds || []).forEach(i => currentGovMap.set(i.id, i));

    const newIds = new Set(
        indicators.map(i => i.id || '').filter(id => id && !id.startsWith('temp-'))
    );

    // 2. SAFE PARTIAL SAVE (FORENSIC SAFE MODE v3 — MODO RECONFIGURACIÓN CONTROLADA ACTIVO)
    const missingIds: string[] = [];
    currentGovMap.forEach((record, id) => {
        if (!newIds.has(id)) missingIds.push(id);
    });

    const hasProtectedIndicatorData = (record: any): boolean => {
        if (!record) return true;
        const label = String(record.name || record.nombre || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toUpperCase();
        if (label.includes('ACCIONES PRIORITARIAS')) return true;

        const logicConfig = record.logic_config;
        if (logicConfig && typeof logicConfig === 'object' && Object.keys(logicConfig).length > 0) return true;
        if (typeof logicConfig === 'string' && logicConfig.trim().length > 0) return true;

        const history = Array.isArray(record.history) ? record.history : [];
        return history.some((h: any) => {
            const metaVal = Number(h?.meta ?? h?.target ?? 0);
            const realVal = Number(h?.real ?? h?.valor ?? h?.value ?? 0);
            const noteVal = typeof (h?.note ?? h?.comentarios ?? h?.notes ?? h?.comment) === 'string'
                ? (h?.note ?? h?.comentarios ?? h?.notes ?? h?.comment).trim()
                : '';
            const hasElements = Array.isArray(h?.elements) && h.elements.length > 0;
            const hasActivities = Array.isArray(h?.activities) && h.activities.length > 0;
            const hasChecklist = Array.isArray(h?.checklist) && h.checklist.length > 0;
            return metaVal > 0 || realVal > 0 || noteVal.length > 0 || hasElements || hasActivities || hasChecklist;
        });
    };

    const protectedMissingIds = missingIds.filter((id) => hasProtectedIndicatorData(currentGovMap.get(id)));
    const isMassiveDrop = currentGovMap.size >= 10 && (
        newIds.size === 0 || newIds.size < currentGovMap.size * 0.5
    );

    const forensicState = {
        universeMismatch: missingIds.length > 0,
        missingIds,
        protectedMissingIds,
        payloadRatio: currentGovMap.size > 0 ? newIds.size / currentGovMap.size : 1,
        duplicateRisk: missingIds.length > 0,
        destructiveBlocked: missingIds.length > 0,
        massiveDropBlocked: isMassiveDrop,
        partialUpdateMode: true,
        deleteRequiresExplicitFlow: true,
        reconfigModeActive: false
    };

    const preserved: string[] = [...missingIds];
    if (missingIds.length > 0) {
        console.warn(isMassiveDrop ? '[PERSIST:CRITICAL_MASSIVE_DROP_BLOCKED]' : '[PERSIST:PARTIAL_UPDATE_SHIELD]', {
            instanceId,
            existingCount: currentGovMap.size,
            payloadCount: newIds.size,
            missingCount: missingIds.length,
            protectedMissingCount: protectedMissingIds.length,
            massiveDropBlocked: isMassiveDrop,
            preservedByShield: preserved.length,
            message: 'Indicadores ausentes conservados. DELETE/ARCHIVE por ausencia esta bloqueado; use DELETE_EXPLICIT en una fase autorizada.'
        });
    }
    /*
     * DELETE/ARCHIVE por ausencia no esta disponible en v18.21.4.
     * La eliminacion requiere una fase futura DELETE_EXPLICIT.
     */
    // 3. Preparar upsert con mapeo consistente, sanitización UTF-8 y campos explícitos
    const toSave = indicators.map((ind, index) => {
        const id = ind.id && !ind.id.startsWith('temp-') ? ind.id : crypto.randomUUID();
        const meta = Number(ind.metaAnual || ind.metaanual || 0) || 1;
        
        let totalReal = 0;
        const sanitizedHistory = (ind.history || []).map((h: any) => {
            const realVal = Number(h.real ?? h.valor ?? 0);
            const metaVal = Number(h.meta ?? h.target ?? 0);
            totalReal += realVal;
            const { valor, periodo, comentarios, month, target, notes, ...cleanFields } = h;
            return {
                ...cleanFields,
                period: sanitizeUTF8(h.period || periodo || month || ''),
                real: realVal,
                meta: metaVal,
                note: sanitizeUTF8(h.note || comentarios || notes || ''),
            };
        });

        const rate = (totalReal / meta) * 100;

        // Recuperar propietario inmutable desde la base de datos si el indicador ya existe
        const dbRecord = dbIndicatorsMap.get(id);
        const finalInstanceId = dbRecord ? dbRecord.instance_id : instanceId;
        const finalCommissionId = dbRecord ? dbRecord.commission_id : instanceId;

        // v18.21.18: Mapeo Canónico Explícito de campos clave con blindaje de propietario
        return {
            id,
            instance_id: finalInstanceId,
            commission_id: finalCommissionId,
            name: sanitizeUTF8(ind.nombre || ind.name || 'Indicador'),
            nombre: sanitizeUTF8(ind.nombre || ind.name || 'Indicador'),
            unit: sanitizeUTF8(ind.unidad || ind.unit || '#'),
            unidad: sanitizeUTF8(ind.unidad || ind.unit || '#'),
            frequency: sanitizeUTF8(ind.frec || ind.frequency || 'Mensual'),
            frec: sanitizeUTF8(ind.frec || ind.frequency || 'Mensual'),
            target_anual: meta,
            metaanual: meta,
            weight: Number(ind.peso ?? ind.weight ?? 0),
            peso: Number(ind.peso ?? ind.weight ?? 0),
            compliance_rate: rate,
            history: sanitizedHistory,
            protocol_type: ind.protocolType || ind.protocol_type || '',
            protocol_actions: ind.protocolActions || ind.protocol_actions || [],
            tipo: ind.tipo || 'Maximizar',
            calculo: ind.calculo || 'Sumar',
            orden: index,
            status: ind.status || 'ACTIVE',
            modo: ind.modo || 'normal',
            logic_config: (() => {
                const config: any = {
                    ...(ind.logic_config || {}),
                    protocol_type: ind.protocolType || ind.protocol_type || '',
                    protocol_actions: ind.protocolActions || ind.protocol_actions || [],
                    tipo: ind.tipo || 'Maximizar',
                    calculo: ind.calculo || 'Sumar',
                    orden: index,
                    modo: ind.modo || 'normal',
                    lastModifiedBy: 'Gobernanza_v18.21.11_CanonicalPersist'
                };
                const existingKey = ind.indicator_key || ind.canonical_key || ind.logic_config?.indicator_key || ind.logic_config?.canonical_key;
                config.indicator_key = existingKey && typeof existingKey === 'string' && existingKey.trim()
                    ? existingKey.trim().toUpperCase()
                    : generateIndicatorKey(ind.nombre || ind.name);
                return config;
            })(),
            updated_at: new Date().toISOString()
        };
    });

    // 4. Deduplicación de último momento
    const uniqueToSave = Array.from(
        toSave.reduce((acc, current) => {
            acc.set(current.id, current);
            return acc;
        }, new Map<string, any>()).values()
    );

    if (uniqueToSave.length > 0) {
        await invokeGovMutation('manage-gov-tracking-scoped', {
            action: 'save_indicators',
            instance_id: instanceId,
            indicators: uniqueToSave
        }, { idempotencyKey: options.idempotencyKey });

        // 6. VERIFICACI??N REAL POST-SAVE
        const savedIds = uniqueToSave.map(u => u.id);
        try {
            const verifyData = await fetchScopedIndicatorsRaw({ indicatorIds: savedIds });
            const normalizeProtocolActions = (actions: any) =>
                Array.isArray(actions)
                    ? actions.map((action: any) => ({
                        accion: String(action?.accion || '').trim(),
                        fecha: String(action?.fecha || '').trim(),
                        notas: String(action?.notas || '').trim(),
                        impacto: String(action?.impacto || '').trim()
                    }))
                    : [];

            const mismatches = uniqueToSave.filter(u => {
                const dbRec: any = verifyData?.find(d => d.id === u.id);
                if (!dbRec) return true;
                const expectedProtocolType = u.protocol_type ?? u.logic_config?.protocol_type ?? '';
                const dbProtocolType = dbRec.protocol_type ?? dbRec.logic_config?.protocol_type ?? '';
                const expectedProtocolActions = normalizeProtocolActions(
                    u.protocol_actions ?? u.logic_config?.protocol_actions
                );
                const dbProtocolActions = normalizeProtocolActions(
                    dbRec.protocol_actions ?? dbRec.logic_config?.protocol_actions
                );
                return Number(dbRec.peso ?? dbRec.weight) !== Number(u.peso) ||
                       dbRec.tipo !== u.tipo ||
                       dbRec.calculo !== u.calculo ||
                       dbProtocolType !== expectedProtocolType ||
                       JSON.stringify(dbProtocolActions) !== JSON.stringify(expectedProtocolActions);
            });

            if (mismatches.length > 0) {
                console.error("[CRITICAL_PERSISTENCE_MISMATCH] Datos en DB no coinciden con lo enviado:", mismatches);
                throw new Error('FALLO DE INTEGRIDAD: Los cambios no se persistieron correctamente en la base de datos.');
            }
            
            if (DEBUG_PERSIST) {
                console.log(`[PERSIST:VERIFIED] ${uniqueToSave.length} indicadores verificados en DB.`);
            }
        } catch (verifyError) {
            console.warn("[PERSIST:VERIFY_WARNING] No se pudo verificar la lectura posterior, pero la escritura principal fue confirmada 2xx:", verifyError);
        }

        console.log(`[PERSIST:SUCCESS_v18.21.4] ${uniqueToSave.length} indicadores guardados. PARTIAL_UPDATE activo: ausentes conservados=${forensicState.missingIds.length}.`);
    }

    if (DEBUG_PERSIST) console.groupEnd();
    
    return forensicState;
};

export const syncIndicatorsSafe = async (
    sourceInstanceId: string,
    targetInstanceIds: string[],
    mode: IndicatorSyncSafeMode = 'CATALOG_AND_ORDER_KEEP_DESTINATION_DATA'
) => {
    if (!sourceInstanceId) throw new Error('SYNC_SAFE: sourceInstanceId requerido.');
    if (!Array.isArray(targetInstanceIds) || targetInstanceIds.length === 0) {
        throw new Error('SYNC_SAFE: seleccione al menos un destino.');
    }
    if (targetInstanceIds.includes(sourceInstanceId)) {
        throw new Error('SYNC_SAFE: el origen no puede estar incluido como destino.');
    }
    if (mode === 'FULL_STRUCTURE_REPLACE_EXPLICIT') {
        throw new Error('FULL_STRUCTURE_REPLACE_EXPLICIT no esta disponible en esta fase.');
    }
    if (!INDICATOR_SYNC_SAFE_MODES.includes(mode)) {
        throw new Error(`SYNC_SAFE: modo no reconocido (${mode}).`);
    }

    const copyOrder = mode === 'CATALOG_AND_ORDER_KEEP_DESTINATION_DATA' || mode === 'CATALOG_ORDER_AND_TARGETS_KEEP_PROGRESS';
    const copyTargets = mode === 'CATALOG_ORDER_AND_TARGETS_KEEP_PROGRESS';
    const sourceIndicators = await getIndicators(sourceInstanceId);
    if (!sourceIndicators.length) {
        throw new Error('SYNC_SAFE: la instancia origen no tiene indicadores.');
    }

    const results: any[] = [];

    for (const targetId of targetInstanceIds) {
        const targetIndicators = await getIndicators(targetId);
        const targetByKey = new Map<string, any>();
        targetIndicators.forEach((indicator: any) => {
            const key = getIndicatorKey(indicator);
            if (key && !targetByKey.has(key)) targetByKey.set(key, indicator);
        });

        const rowsToUpsert: any[] = [];
        const missingToCreate: string[] = [];
        const orderChanges: string[] = [];
        const targetCopies: string[] = [];
        const preservedFields = ['real', 'note', 'elements', 'activities', 'checklist', 'logic_config'];

        sourceIndicators.forEach((sourceIndicator: any, index: number) => {
            const key = getIndicatorKey(sourceIndicator);
            const existing = targetByKey.get(key);
            const safeHistory = existing
                ? syncHistoryKeepDestinationProgress(existing.history, sourceIndicator.history, copyTargets)
                : buildNewIndicatorHistoryFromSource(sourceIndicator.history, copyTargets);
            const meta = safeHistory.reduce((acc: number, h: any) => acc + (Number(h.meta) || 0), 0);
            const id = existing?.id || crypto.randomUUID();

            if (!existing) missingToCreate.push(sourceIndicator.nombre || sourceIndicator.name || id);
            if (copyOrder && existing && Number(existing.orden ?? 0) !== index) orderChanges.push(existing.nombre || existing.name || id);
            if (copyTargets) targetCopies.push(sourceIndicator.nombre || sourceIndicator.name || id);

            rowsToUpsert.push({
                id,
                instance_id: targetId,
                commission_id: targetId,
                name: sanitizeUTF8(sourceIndicator.nombre || sourceIndicator.name || 'Indicador'),
                nombre: sanitizeUTF8(sourceIndicator.nombre || sourceIndicator.name || 'Indicador'),
                unit: sanitizeUTF8(sourceIndicator.unidad || sourceIndicator.unit || '#'),
                unidad: sanitizeUTF8(sourceIndicator.unidad || sourceIndicator.unit || '#'),
                frequency: sanitizeUTF8(sourceIndicator.frec || sourceIndicator.frequency || 'Mensual'),
                frec: sanitizeUTF8(sourceIndicator.frec || sourceIndicator.frequency || 'Mensual'),
                target_anual: copyTargets ? meta : Number(existing?.metaAnual ?? existing?.metaanual ?? existing?.target_anual ?? 0),
                metaanual: copyTargets ? meta : Number(existing?.metaanual ?? existing?.metaAnual ?? existing?.target_anual ?? 0),
                weight: Number(sourceIndicator.peso ?? sourceIndicator.weight ?? 0),
                peso: Number(sourceIndicator.peso ?? sourceIndicator.weight ?? 0),
                compliance_rate: Number(existing?.compliance_rate ?? 0),
                history: safeHistory,
                tipo: sourceIndicator.tipo || existing?.tipo || 'Maximizar',
                calculo: sourceIndicator.calculo || existing?.calculo || 'Sumar',
                orden: copyOrder ? index : Number(existing?.orden ?? targetIndicators.length + index),
                status: existing?.status || 'ACTIVE',
                modo: sourceIndicator.modo || existing?.modo || 'normal',
                logic_config: {
                    ...(typeof existing?.logic_config === 'object' && existing?.logic_config ? existing.logic_config : {}),
                    ...(existing ? {} : (typeof sourceIndicator.logic_config === 'object' && sourceIndicator.logic_config ? sourceIndicator.logic_config : {})),
                    sync_safe_mode: mode,
                    sync_safe_source_id: sourceInstanceId,
                    sync_safe_updated_at: new Date().toISOString()
                },
                updated_at: new Date().toISOString()
            });
        });

        if (rowsToUpsert.length > 0) {
            await invokeGovMutation('manage-gov-tracking-scoped', {
                action: 'save_indicators',
                instance_id: targetId,
                indicators: rowsToUpsert
            });
        }

        results.push({
            targetId,
            existingIndicators: targetIndicators.length,
            sourceIndicators: sourceIndicators.length,
            created: missingToCreate.length,
            updated: rowsToUpsert.length - missingToCreate.length,
            orderChanges: orderChanges.length,
            targetsCopied: targetCopies.length,
            missingCreated: missingToCreate,
            fieldsNotTouched: preservedFields,
            deleteArchiveBlocked: true
        });
    }

    return {
        mode,
        sourceInstanceId,
        targetCount: targetInstanceIds.length,
        sourceIndicatorCount: sourceIndicators.length,
        results
    };
};

/**
 * Replica los indicadores de una instancia a todas las demás del mismo tipo o a toda la organización
 */
// LEGACY PELIGROSO: no usar para sincronizacion de orden. Puede replicar estructura completa.
export const replicateIndicatorsToAll = async (sourceInstanceId: string, allInstanceIds: string[]) => {
    console.log(`[AUDIT] Starting selective replica from ${sourceInstanceId} to ${allInstanceIds.length} targets`);
    
    const sourceIndicators = await getIndicators(sourceInstanceId);
    console.log(`${sourceIndicators.length} source indicators`);

    if (!sourceIndicators.length) {
        console.warn(`[AUDIT] Source instance ${sourceInstanceId} has no indicators to replicate.`);
        throw new Error('La instancia origen no tiene indicadores para replicar.');
    }

    const targetInstances = allInstanceIds.filter(id => id !== sourceInstanceId);
    console.log(`[AUDIT] Replicating to ${targetInstances.length} target instances:`, targetInstances);

    let successCount = 0;
    let errorCount = 0;

    for (const targetId of targetInstances) {
        try {
            console.log(`[AUDIT] Processing target: ${targetId}`);
            // 1. Obtener indicadores actuales del destino para intentar emparejar por nombre y preservar datos
            // FIX CRÍTICO v14.5.50: getIndicators() devuelve un Array directamente, NO un objeto {data}.
            // El destructuring anterior { data: targetIndsRaw } siempre producía undefined.
            const targetInds: any[] = await getIndicators(targetId);
            
            const matchedIds = new Set<string>();
            const replicated = sourceIndicators.map(ind => {
                // Buscamos si ya existe uno con el mismo nombre para NO perder sus registros históricos (history real)
                // Usamos normalize para ignorar acentos y ñ en el emparejamiento
                const existing = targetInds.find(t => 
                    normalize(t.nombre) === normalize(ind.nombre) && 
                    !matchedIds.has(t.id)
                );
                
                if (existing) matchedIds.add(existing.id);
                
                return {
                    ...ind,
                    id: existing ? existing.id : crypto.randomUUID(),
                    instance_id: targetId,
                    // Si ya existía, conservamos su historial de resultados reales (real), pero actualizamos la meta (meta)
                    history: existing && Array.isArray(existing.history) ? existing.history.map((h: any) => {
                        const sourceItem = ind.history.find((sh: any) => sh.period === h.period);
                        return { ...h, meta: sourceItem ? sourceItem.meta : 0 };
                    }) : ind.history.map((h: any) => ({ ...h, real: 0 })),
                    updated_at: new Date().toISOString()
                };
            });

            console.log(`[AUDIT] Saving ${replicated.length} replicated indicators to target ${targetId}`);
            await saveIndicators(targetId, replicated);
            successCount++;
        } catch (err) {
            console.error(`[AUDIT] Error replicating to ${targetId}:`, err);
            errorCount++;
        }
    }
    
    console.log(`[AUDIT] Replica process finished. Success: ${successCount}, Errors: ${errorCount}`);
    if (errorCount > 0 && successCount === 0) {
        throw new Error('No se pudo replicar a ninguna de las instancias seleccionadas.');
    }
};

export const deleteIndicator = async (id: string, bypassShield = false) => {
    if (!bypassShield) {
        const scopedIndicators = await fetchScopedIndicatorsRaw({ indicatorIds: [id] });
        const indicator = scopedIndicators[0];
        const protectedOwnerId = String(
            indicator?.instance_id || indicator?.commission_id || indicator?.unified_instance_id || ''
        ).trim();
        if (protectedOwnerId && PROTECTED_COMMISSIONS.includes(protectedOwnerId)) {
            throw new Error('PROTECCI??N DE SEGURIDAD: Indicador cr??tico blindado. Solicite autorizaci??n para su eliminaci??n.');
        }
    }

    await invokeGovMutation('manage-gov-tracking-scoped', {
        action: 'delete_indicator',
        indicator_id: id
    });
};

// ─── AI Context ──────────────────────────────────────────
export const getAIContext = async (user: User) => {
    const [hierarchy, stats, talent, reports, memory] = await Promise.all([
        getHierarchy(user),
        user.role === 'SUPER_ADMIN' ? getDashboardPresidencia() : getDashboardByCommission(user),
        getTalent(),
        getTracking(user),
        getMemory(user)
    ]);

    return JSON.stringify({
        org_name: 'COPARMEX Gobernanza Articulada',
        user_role: user.role,
        hierarchy_summary: {
            vps: hierarchy.vicepresidencies.length,
            comms: hierarchy.commissions.length,
            independents: hierarchy.independents.length
        },
        stats,
        talent_pool_size: talent.length,
        recent_reports: reports.slice(0, 5),
        document_count: memory.length
    }, null, 2);
};

// --- MEETINGS MANAGEMENT ---

export const getMeetings = async (params?: { user_id?: string; vp_id?: string; comm_id?: string }): Promise<Meeting[]> => {
    let query = supabase.from('gov_meetings').select('*');
    if (params?.user_id) query = query.eq('owner_id', params.user_id);
    if (params?.vp_id) query = query.eq('vicepresidency_id', params.vp_id);
    if (params?.comm_id) query = query.eq('commission_id', params.comm_id);

    const { data, error } = await query.order('date', { ascending: false });
    if (error) throw error;
    return data || [];
};

export const createMeeting = async (meeting: Partial<Meeting>): Promise<Meeting> => {
    const { data, error } = await supabase.from('gov_meetings')
        .insert(meeting)
        .select()
        .single();
    if (error) throw error;
    return data;
};

export const updateMeeting = async (id: string, meeting: Partial<Meeting>): Promise<Meeting> => {
    const { data, error } = await supabase.from('gov_meetings')
        .update(meeting)
        .eq('id', id)
        .select()
        .single();
    if (error) throw error;
    return data;
};

export const deleteMeeting = async (id: string): Promise<void> => {
    const { error } = await supabase.from('gov_meetings').delete().eq('id', id);
    if (error) throw error;
};

/**
 * Calcula el cumplimiento de un indicador basado en su historial y meta anual.
 */
export const calculateIndicatorCompliance = (ind: any, settings?: any) => {
    const history = Array.isArray(ind.history) ? ind.history : [];
    const targetAnual = Number(ind.target_anual || ind.metaanual || ind.meta || 0);
    const sumReal = history.reduce((acc: number, h: any) => acc + (Number(h.real) || 0), 0);
    
    if (targetAnual === 0) return 0;
    
    const rawCompliance = (sumReal / targetAnual) * 100;
    return Math.round(Math.min(rawCompliance, 100)); // Capeado al 100% por estándar ejecutivo
};

/**
 * Guarda el orden de los indicadores con UPDATE puro.
 * No usa UPSERT: la persistencia de orden nunca debe insertar indicadores.
 */
export const saveIndicatorOrder = async (indicators: any[]) => {
    if (!indicators.length) return;
    return invokeGovMutation('manage-gov-tracking-scoped', {
        action: 'save_order',
        indicators: indicators.map((ind, index) => ({ id: ind.id, orden: Number(ind.orden ?? index) }))
    });
};

/**
 * Guarda solo propiedades editables del indicador con UPDATE puro.
 * No toca history, metas mensuales, avances, notas, checklist, orden ni logic_config.
 */
export const saveIndicatorProperties = async (indicators: any[]) => {
    if (!Array.isArray(indicators)) {
        throw new Error('Los indicadores proporcionados no son un arreglo valido');
    }
    if (!indicators.length) return { requested: 0, updated: 0 };

    return invokeGovMutation('manage-gov-tracking-scoped', {
        action: 'save_properties',
        indicators
    });
};

/**
 * Actualiza el valor real y la nota de un indicador para un periodo específico.
 * v18.4.1-REAL-RESTORE: Implementación canónica.
 */
export const updateIndicatorValue = async (indicatorId: string, month: string, real: number, note: string, allChecklists?: Record<string, any[]>) => {
    // 1. Obtener el indicador actual desde el backend autenticado
    const ind = (await fetchScopedIndicatorsRaw({ indicatorIds: [indicatorId] }))[0];
    
    if (!ind) {
        throw new Error("Indicador no encontrado en gov_indicators");
    }

    // 2. Actualizar history
    let history = Array.isArray(ind.history) ? [...ind.history] : [];
    
    if (allChecklists) {
        // ACTUALIZACIÓN MASIVA (Checklist Anual - Imagen 3)
        history = history.map((h: any) => {
            const period = h.period || h.month || '';
            const checklist = allChecklists[period];
            if (checklist) {
                const completed = checklist.filter((i: any) => i.completed).length;
                return { ...h, checklist, real: completed, meta: checklist.length };
            }
            return h;
        });
    } else {
        // ACTUALIZACIÓN MENSUAL ÚNICA
        const monthIdx = history.findIndex((h: any) => 
            (h.period || h.month || '').toString().toUpperCase() === month.toUpperCase()
        );

        if (monthIdx !== -1) {
            history[monthIdx] = { ...history[monthIdx], real, note };
        } else {
            history.push({ period: month.toUpperCase(), real, note, meta: 0 });
        }
    }

    // 3. Recalcular cumplimiento anual
    const targetAnual = Number(ind.target_anual || ind.metaanual || 1);
    const sumReal = history.reduce((acc: number, h: any) => acc + (Number(h.real) || 0), 0);
    const complianceRate = targetAnual > 0 ? (sumReal / targetAnual) * 100 : 0;

    // 4. Update en ambas tablas
    const updatePayload = { 
        history, 
        compliance_rate: complianceRate,
        last_value: allChecklists ? history.find(h => h.period === month)?.real || real : real,
        updated_at: new Date().toISOString()
    };

    await invokeGovMutation('manage-gov-tracking-scoped', {
        action: 'update_indicator_capture',
        indicator_id: indicatorId,
        changes: updatePayload
    });
};

/**
 * Recupera los totales vivos de los planes de trabajo por ID de comisión.
 * v18.9.8: Implementación de modelo Plan VP con cálculo directo de secciones.
 */
export const getWorkPlanLiveTotalsByCommissionIdsLegacy = async (commissionIds: string[], year = 2026) => {
    return getWorkPlanLiveTotalsByCommissionIds(commissionIds, year);
};

export const getWorkPlanLiveTotalsByCommissionIds = async (commissionIds: string[], year = 2026) => {
    if (!commissionIds.length) return [];
    const plans = await fetchScopedWorkPlans({
        commissionIds,
        year,
        includeLiveTotals: true
    });

    const totals: Record<string, any> = {};
    commissionIds.forEach(id => {
        totals[id] = {
            commission_id: id,
            total_goal: 0,
            total_progress: 0,
            planPct: 0,
            planEligible: false,
            source: "none"
        };
    });

    plans.forEach((plan: any) => {
        const commId = String(plan.commission_id || '');
        if (commId && totals[commId]) {
            totals[commId].total_goal += Number(plan.live_total_goal ?? 0);
            totals[commId].total_progress += Number(plan.live_total_progress ?? 0);
        }
    });

    Object.keys(totals).forEach(id => {
        const t = totals[id];
        if (t.total_goal > 0) {
            t.planPct = Math.round((t.total_progress / t.total_goal) * 100);
            t.planEligible = t.total_goal > 0 && t.total_progress > 0 && t.planPct > 0;
            t.source = "gov_work_plan_sections_live";
        }
    });

    return Object.values(totals);
};

/**
 * Alias para getIndicators usado en TrackingModule (v18.9.98 Restore)
 */
export const getInstanceIndicators = async (instanceId: string) => {
    return getIndicators(instanceId);
};

/**
 * Recupera el detalle del plan activo para una instancia (v18.9.98 Restore)
 */
export const getInstancePlanLegacy = async (instanceId: string, year = 2026) => {
    return getInstancePlan(instanceId, year);
};

export const getInstancePlan = async (instanceId: string, year = 2026) => {
    const plans = await fetchScopedWorkPlans({
        commissionIds: [instanceId],
        year
    });
    const plan = plans[0];

    if (!plan) return [];

    const detail = await getWorkPlanDetail(plan.id);
    return requireScopedArray(detail?.sections, 'plan.sections');
};

/**
 * Actualiza el historial completo de un indicador y recalcula su cumplimiento.
 */
export const updateIndicatorHistory = async (indicatorId: string, history: any[]) => {
    const ind = (await fetchScopedIndicatorsRaw({ indicatorIds: [indicatorId] }))[0];
    if (!ind) throw new Error("Indicador no encontrado");

    const targetAnual = Number(ind.target_anual || ind.metaanual || 1);
    const sumReal = history.reduce((acc: number, h: any) => acc + (Number(h.real || h.valor || 0)), 0);
    const complianceRate = targetAnual > 0 ? (sumReal / targetAnual) * 100 : 0;

    const updatePayload = { 
        history, 
        compliance_rate: complianceRate,
        updated_at: new Date().toISOString()
    };

    await invokeGovMutation('manage-gov-tracking-scoped', {
        action: 'update_indicator_capture',
        indicator_id: indicatorId,
        changes: updatePayload
    });
};

/**
 * planningFlexibleSyncDryRun (v18.21.44)
 * Motor Real de Dry-Run de Sincronización Flexible en Modo Solo Lectura.
 * Compara planes, secciones y columnas reales de Supabase sin realizar escrituras.
 */
export const planningFlexibleSyncDryRun = async (params: {
    sourcePlanId: string;
    targetInstanceIds?: string[];
    targetCommissionIds?: string[];
    selectedSectionIds: (number | string)[];
    modeBySection: Record<string, string>;
    preserveCapturedData?: boolean;
    preserveDestinationOrphans?: boolean;
    includeMetas?: boolean;
    dryRunOnly: true;
}) => {
    if (params.dryRunOnly !== true) {
        throw new Error("CRITICAL_SECURITY_VIOLATION: La ejecuci??n del dry-run flexible de planeaci??n requiere obligatoriamente el flag dryRunOnly en true.");
    }

    const targetIds = params.targetInstanceIds || params.targetCommissionIds || [];
    const logs: string[] = [];
    logs.push(`[DRY-RUN ENGINE] Iniciando auditor??a estructural de solo lectura...`);

    let sourcePlan: any = null;
    try {
        sourcePlan = await fetchScopedWorkPlanDetailRaw(params.sourcePlanId, { includeItems: true });
    } catch (pError: any) {
        return {
            success: false,
            logs: [`[ERROR] Plan origen no encontrado: ${pError?.message || 'ID inv??lido'}`],
            finalRecommendation: "Corrige el ID del plan origen.",
            warnings: ["Plan origen inexistente"],
            blockers: [{ reason: "Plan origen no encontrado", code: "INVALID_SOURCE" }],
            sectionComparisons: [],
            targetSummaries: [],
            estimatedWrites: { inserts: 0, updates: 0, deletes: 0 }
        };
    }

    if (!sourcePlan) {
        return {
            success: false,
            logs: [`[ERROR] Plan origen no encontrado: ID inv??lido`],
            finalRecommendation: "Corrige el ID del plan origen.",
            warnings: ["Plan origen inexistente"],
            blockers: [{ reason: "Plan origen no encontrado", code: "INVALID_SOURCE" }],
            sectionComparisons: [],
            targetSummaries: [],
            estimatedWrites: { inserts: 0, updates: 0, deletes: 0 }
        };
    }

    const activeSections = sourcePlan.sections || [];
    logs.push(`[DRY-RUN] Plan origen "${sourcePlan.commission_name || 'Maestro'}" cargado con ${activeSections.length} secciones.`);
    const activeItems = sourcePlan.items || [];

    const targetSummaries: any[] = [];
    const sectionComparisons: any[] = [];
    const blockers: any[] = [];
    const warnings: any[] = [];

    let totalInserts = 0;
    let totalUpdates = 0;
    let totalDeletes = 0;

    for (const targetId of targetIds) {
        const { data: targetComm } = await supabase
            .from('gov_commissions')
            .select('name, status, type')
            .eq('id', targetId)
            .maybeSingle();

        const destName = targetComm?.name || `Comisi??n ${targetId}`;
        const targetType = targetComm?.type ? normalizeVPType(targetComm.type) : 'DESCONOCIDO';
        logs.push(`[AUDIT] Comparando destino: "${destName}" (${targetType})...`);

        if (targetType === 'DESCONOCIDO') {
            blockers.push({
                targetInstanceId: targetId,
                reason: `Instancia "${destName}" tiene un tipo de destino desconocido o ausente en BD.`,
                code: 'UNKNOWN_TYPE'
            });
            logs.push(`[BLOCKER] "${destName}" tiene tipo de destino desconocido.`);
        }

        const hasS5Selected = params.selectedSectionIds.some(id => String(id) === '5');
        const excludedSectionsForTarget: Array<{section: string; sectionNumber: number; reason: string; targetType: string; message: string}> = [];

        if (hasS5Selected && (targetType === 'COMITE' || targetType === 'CONSEJERIA')) {
            excludedSectionsForTarget.push({
                section: 'S5',
                sectionNumber: 5,
                reason: 'INVALID_S5_TARGET',
                targetType,
                message: `S5 "Comisiones Locales" no aplica para instancias de tipo ${targetType}. Excluida autom??ticamente de la sincronizaci??n hacia "${destName}".`
            });
            warnings.push(`[S5-EXCLUIDA] "${destName}" (${targetType}): S5 "Comisiones Locales" excluida por incompatibilidad de tipo. El resto de secciones contin??a.`);
            logs.push(`[INFO S5] S5 excluida tolerantemente para "${destName}" (${targetType}). No se bloquea el dry-run.`);
        } else if (hasS5Selected && targetType === 'COMISION') {
            warnings.push(`La secci??n "Comisiones Locales" (S5) ser?? sincronizada a la Comisi??n "${destName}" con estatus de revisi??n.`);
            logs.push(`[Advertencia S5] S5 Comisiones Locales seleccionada para la Comisi??n "${destName}".`);
        }

        const resolvedTargetPlans = await resolvePointWorkPlans([targetId], sourcePlan.year);
        const destPlanRef = resolvedTargetPlans[targetId];

        let destPlan: any = null;
        let destSections: any[] = [];
        let destItems: any[] = [];
        if (destPlanRef?.plan_id) {
            destPlan = await fetchScopedWorkPlanDetailRaw(destPlanRef.plan_id, { includeItems: true });
            destSections = destPlan?.sections || [];
            destItems = destPlan?.items || [];
        }

        if (destPlan && (destPlan.vp_approved || destPlan.status === 'APROBADO')) {
            blockers.push({
                targetInstanceId: targetId,
                reason: `Instancia "${destName}" tiene su plan ya APROBADO o con aval activo.`,
                code: 'USER_LOCKED'
            });
            logs.push(`[BLOCKER] "${destName}" tiene el plan bloqueado (aprobado).`);
        }

        for (const srcSec of activeSections) {
            const isSelected = params.selectedSectionIds.some(
                id => String(id) === String(srcSec.id) || String(id) === String(srcSec.section_number) || String(id) === String(srcSec.order_index)
            );

            if (!isSelected) {
                sectionComparisons.push({
                    sectionId: srcSec.id,
                    sectionName: srcSec.name,
                    actionRequired: 'EXCLUDE',
                    riskLevel: 'BAJO'
                });
                continue;
            }

            const isSectionS5 = String(srcSec.section_number) === '5';
            if (isSectionS5 && excludedSectionsForTarget.some(e => e.sectionNumber === 5)) {
                sectionComparisons.push({
                    sectionId: srcSec.id,
                    sectionName: srcSec.name,
                    actionRequired: 'EXCLUDE',
                    riskLevel: 'BAJO',
                    details: `S5 "Comisiones Locales" excluida autom??ticamente: incompatible con ${targetType}. No aplica sincronizaci??n hacia "${destName}".`,
                    excludedReason: 'INVALID_S5_TARGET'
                });
                logs.push(`[S5-EXCLUDE] S5 omitida en comparaci??n para "${destName}" (${targetType}).`);
                continue;
            }

            const mode = params.modeBySection[srcSec.section_number || ''] || 'SOLO_ESTRUCTURA_FORMATO';
            const destSecMatch = destSections.find(ds => String(ds.section_number) === String(srcSec.section_number));

            if (!destSecMatch) {
                totalInserts++;
                sectionComparisons.push({
                    sectionId: srcSec.id,
                    sectionName: srcSec.name,
                    actionRequired: 'CREAR',
                    riskLevel: 'BAJO',
                    details: `Se crear?? la secci??n en el destino en modo: ${mode}`
                });
                logs.push(`[SECCI??N] S${srcSec.section_number} "${srcSec.name}" se AGREGAR?? en "${destName}".`);
            } else {
                totalUpdates++;
                let risk: 'BAJO' | 'MEDIO' | 'CRITICO' = 'BAJO';

                const hasLocalData = destSecMatch.progress > 0 || destSecMatch.compliance > 0;
                if (hasLocalData) {
                    risk = 'MEDIO';
                    warnings.push(`La secci??n S${srcSec.section_number} en "${destName}" ya tiene datos de avance locales.`);
                    logs.push(`[Riesgo Medio] S${srcSec.section_number} en "${destName}" ya tiene avances cargados.`);
                }

                const sourceSecItems = activeItems.filter(i => i.section_id === srcSec.id);
                const destSecItems = destItems.filter(i => i.section_id === destSecMatch.id);

                for (const dItem of destSecItems) {
                    const matchByName = sourceSecItems.some(si => normalize(si.title) === normalize(dItem.title));
                    const matchById = sourceSecItems.some(si => si.id === dItem.id);

                    if (matchByName && !matchById) {
                        risk = 'CRITICO';
                        warnings.push(`Inestabilidad de ID: Colisi??n por nombre en elemento "${dItem.title}" de la secci??n S${srcSec.section_number} en "${destName}".`);
                        logs.push(`[Riesgo Alto] Colisi??n por t??tulo sin ID estable para elemento "${dItem.title}" en S${srcSec.section_number}.`);
                    }
                }

                sectionComparisons.push({
                    sectionId: srcSec.id,
                    sectionName: srcSec.name,
                    actionRequired: 'ACTUALIZAR',
                    riskLevel: risk,
                    details: `Se actualizar?? estructura en modo ${mode}. Conservando datos capturados locales.`
                });
            }
        }

        for (const dSec of destSections) {
            const matchInSrc = activeSections.some(ss => String(ss.section_number) === String(dSec.section_number));
            if (!matchInSrc) {
                const preserve = params.preserveDestinationOrphans !== false;
                if (preserve) {
                    sectionComparisons.push({
                        sectionId: dSec.id,
                        sectionName: dSec.name,
                        actionRequired: 'PRESERVAR',
                        riskLevel: 'BAJO',
                        details: 'Secci??n local hu??rfana. Preservada por exclusi??n l??gica.'
                    });
                    logs.push(`[HU??RFANO] Preservando secci??n local hu??rfana S${dSec.section_number} "${dSec.name}" en "${destName}".`);
                } else {
                    totalDeletes++;
                    sectionComparisons.push({
                        sectionId: dSec.id,
                        sectionName: dSec.name,
                        actionRequired: 'ELIMINAR',
                        riskLevel: 'CRITICO',
                        details: 'Secci??n local hu??rfana. Ser??a eliminada para aplicar estructura del origen.'
                    });
                    logs.push(`[HU??RFANO] Secci??n local hu??rfana S${dSec.section_number} "${dSec.name}" en "${destName}" se ELIMINAR??.`);
                }
            }
        }

        targetSummaries.push({
            instanceId: targetId,
            name: destName,
            type: targetType,
            totalSectionsDestino: destSections.length,
            excludedSections: excludedSectionsForTarget,
            statusGlobal: blockers.some(b => b.targetInstanceId === targetId) ? 'Bloqueado' : (excludedSectionsForTarget.length > 0 || warnings.length > 0 ? 'Revisi??n' : 'Seguro')
        });
    }

    logs.push(`[VEREDICTO] Simulaci??n completada con ??xito. Cero escrituras realizadas en Supabase.`);

    return {
        success: true,
        sourceSummary: {
            instanceId: sourcePlan.commission_id,
            name: sourcePlan.commission_name || 'Plan Origen',
            totalSections: activeSections.length
        },
        targetSummaries,
        sectionComparisons,
        blockers,
        warnings,
        estimatedWrites: {
            inserts: totalInserts,
            updates: totalUpdates,
            deletes: totalDeletes
        },
        logs,
        finalRecommendation: blockers.length > 0 
            ? "Operaci??n bloqueada temporalmente. Resuelva los blockers cr??ticos antes de proceder."
            : "Estructura compatible. Simulaci??n de Dry-Run exitosa (cero escrituras)."
    };
};

/**
 * planningFlexibleSyncPreflight (v18.21.48)
 * Fase intermedia: preflight/checkpoint preview en memoria, sin escribir en Supabase.
 */
export const planningFlexibleSyncPreflight = async (params: {
    sourcePlanId: string;
    targetInstanceIds?: string[];
    targetCommissionIds?: string[];
    selectedSectionIds: (number | string)[];
    modeBySection: Record<string, string>;
    dryRunResult?: any;
    dryRunOnly: true;
    checkpointPreviewOnly: true;
    preserveCapturedData?: boolean;
}) => {
    if (params.dryRunOnly !== true || params.checkpointPreviewOnly !== true) {
        throw new Error("CRITICAL_SECURITY_VIOLATION: El preflight requiere dryRunOnly y checkpointPreviewOnly en true.");
    }

    const targetIds = params.targetInstanceIds || params.targetCommissionIds || [];
    
    const dryRun = params.dryRunResult || await planningFlexibleSyncDryRun({
        sourcePlanId: params.sourcePlanId,
        targetInstanceIds: targetIds,
        selectedSectionIds: params.selectedSectionIds,
        modeBySection: params.modeBySection,
        preserveCapturedData: params.preserveCapturedData ?? true,
        preserveDestinationOrphans: true,
        includeMetas: true,
        dryRunOnly: true
    });

    let canExecuteFuture = true;
    const blockedReasons: string[] = [];

    if (dryRun.blockers && dryRun.blockers.length > 0) {
        canExecuteFuture = false;
        blockedReasons.push("Existen blockers cr??ticos en el an??lisis estructural.");
    }
    
    if (dryRun.estimatedWrites && dryRun.estimatedWrites.deletes > 0) {
        canExecuteFuture = false;
        blockedReasons.push("El motor detect?? operaciones DELETE, lo cual est?? prohibido.");
    }

    if (params.preserveCapturedData === false) {
        canExecuteFuture = false;
        blockedReasons.push("preserveCapturedData debe estar garantizado (true) para la ejecuci??n futura.");
    }

    const checkpointPreview: any[] = [];
    const rollbackPreview: any[] = [];

    for (const targetId of targetIds) {
        const targetPlans = await fetchScopedWorkPlans({ commissionIds: [targetId] });
        const destPlan = [...(targetPlans || [])]
            .sort((a: any, b: any) => Number(b?.year || 0) - Number(a?.year || 0))[0] || null;

        if (destPlan?.id) {
            const destPlanDetail = await fetchScopedWorkPlanDetailRaw(destPlan.id);
            const destSections = destPlanDetail?.sections || [];

            (destSections || []).forEach(sec => {
                const isAffected = (dryRun.sectionComparisons || []).some((c: any) => 
                    (c.actionRequired === 'ACTUALIZAR' || c.actionRequired === 'CREAR') && 
                    (String(c.sectionId) === String(sec.id) || c.sectionName === sec.name || String(c.sectionName) === String(sec.section_number))
                );

                if (isAffected) {
                    checkpointPreview.push({
                        work_plan_id: destPlan.id,
                        commission_id: targetId,
                        section_id: sec.id,
                        section_number: sec.section_number,
                        name: sec.name,
                        content: sec.content,
                        goal: sec.goal,
                        meta_anual: sec.meta_anual,
                        progress: sec.progress,
                        total_goal: destPlan.total_goal,
                        total_progress: destPlan.total_progress,
                        timestamp: new Date().toISOString(),
                        motivo: "Snapshot previo a modificaci??n de estructura"
                    });

                    rollbackPreview.push({
                        section_name: sec.name,
                        target_instance: targetId,
                        campo: "content, meta_anual",
                        valor_actual: "Versi??n que ser??a modificada",
                        valor_rollback: "Versi??n JSON original preservada en snapshot",
                        riesgo: "BAJO",
                        dependencia: "gov_audit_log"
                    });
                }
            });
        }
    }

    return {
        preflightStatus: canExecuteFuture ? "READY_FOR_EXECUTION" : "BLOCKED",
        canExecuteFuture,
        blockers: dryRun.blockers || [],
        warnings: dryRun.warnings || [],
        checkpointPreview,
        rollbackPreview,
        affectedTargets: targetIds,
        affectedSections: (dryRun.sectionComparisons || []).filter((c: any) => c.actionRequired === 'CREAR' || c.actionRequired === 'ACTUALIZAR'),
        blockedReasons,
        finalRecommendation: canExecuteFuture 
            ? "Preflight superado. Los checkpoints en memoria est??n listos para respaldar la ejecuci??n futura."
            : "Preflight fallido. Revisa los blockedReasons antes de intentar ejecutar."
    };
};

/**
 * planningFlexibleSyncExecute (v18.21.51)
 * Estructura inerte de ejecución final DML
 */
export const planningFlexibleSyncExecute = async (params: {
    sourcePlanId: string;
    targetInstanceIds: string[];
    selectedSectionIds: (number | string)[];
    modeBySection: Record<string, string>;
    approvedDryRunId?: string;
    approvedPreflightId?: string;
    preserveCapturedData: true;
    preserveDestinationOrphans: true;
    checkpointRequired: true;
    finalUserConfirmation: string;
    executionEnabled: boolean;
    dryRunOnly?: boolean;
}) => {
    // 1. Guardias de entorno global
    const FLEXIBLE_SYNC_EXECUTION_ENABLED = false;
    const BLOCK_SYNC_IS_ACTIVE = true; // Hardcoded para la v18.21.51 hasta que se mueva a configuracion

    if (BLOCK_SYNC_IS_ACTIVE) {
        return {
            success: false,
            status: "EXECUTION_DISABLED",
            reason: "BLOCK_SYNC_ACTIVE",
            writesExecuted: 0,
            inserts: 0,
            updates: 0,
            deletes: 0,
            message: "La ejecución está deshabilitada porque BLOCK_SYNC se encuentra activo protegiendo la base de datos."
        };
    }

    if (!FLEXIBLE_SYNC_EXECUTION_ENABLED) {
        return {
            success: false,
            status: "EXECUTION_DISABLED",
            reason: "FEATURE_FLAG_DISABLED",
            writesExecuted: 0,
            inserts: 0,
            updates: 0,
            deletes: 0,
            message: "La función está deshabilitada por el Feature Flag global."
        };
    }

    // 2. Guardias de Parámetros
    if (params.executionEnabled !== true) {
        throw new Error("EXECUTION_ABORTED: executionEnabled !== true");
    }
    if (params.checkpointRequired !== true) {
        throw new Error("EXECUTION_ABORTED: checkpointRequired !== true");
    }
    if (params.preserveCapturedData !== true) {
        throw new Error("EXECUTION_ABORTED: preserveCapturedData !== true");
    }
    if (params.preserveDestinationOrphans !== true) {
        throw new Error("EXECUTION_ABORTED: preserveDestinationOrphans !== true");
    }
    if (params.finalUserConfirmation !== "AUTORIZO SINCRONIZACION FLEXIBLE CONTROLADA") {
        throw new Error("EXECUTION_ABORTED: finalUserConfirmation incorrecta");
    }
    if (!params.approvedDryRunId) {
        throw new Error("EXECUTION_ABORTED: No hay approvedDryRunId");
    }
    if (!params.approvedPreflightId) {
        throw new Error("EXECUTION_ABORTED: No hay approvedPreflightId");
    }

    // 3. Regla crítica de estructura inerte
    return {
        success: false,
        status: "EXECUTION_NOT_IMPLEMENTED_IN_THIS_VERSION",
        reason: "INERT_SKELETON",
        writesExecuted: 0,
        inserts: 0,
        updates: 0,
        deletes: 0,
        message: "Las guardias pasaron, pero el motor DML no está programado. Cero escrituras."
    };
};

/**
 * planningFlexibleSyncCreateCheckpoint (v18.21.54)
 * Estructura inerte para futura creación del Snapshot de Seguridad
 */
/**
 * planningFlexibleSyncCreateCheckpoint (v18.21.60)
 * Implementación de la creación del Snapshot de Seguridad (Gated, target: gov_audit_log)
 */
export const planningFlexibleSyncCreateCheckpoint = async (params: {
    sourcePlanId: string;
    targetInstanceIds: string[];
    selectedSectionIds: (number | string)[];
    modeBySection: Record<string, string>;
    approvedDryRunId?: string;
    approvedPreflightId?: string;
    checkpointPreview?: any[];
    rollbackPreview?: any[];
    finalUserConfirmation: string;
    checkpointOnly: true;
    checkpointPreviewOnly?: boolean;
    executionEnabled: false;
    flexibleSyncCheckpointEnabled: boolean;
    userId?: string;
    createdBy?: string;
}) => {
    // 1. Guardias de entorno global
    const FLEXIBLE_SYNC_CHECKPOINT_WRITE_ENABLED = false; // FLAG BLOQUEADO POR DEFECTO

    if (!FLEXIBLE_SYNC_CHECKPOINT_WRITE_ENABLED) {
        return {
            success: false,
            status: "CHECKPOINT_DISABLED_BY_FLAG",
            checkpointCreated: false,
            checkpointId: null,
            writesExecuted: 0,
            inserts: 0,
            updates: 0,
            deletes: 0,
            message: "La creación de checkpoint está deshabilitada por el Feature Flag global."
        };
    }

    // 2. Guardias de Parámetros
    if (params.flexibleSyncCheckpointEnabled !== true) {
        throw new Error("CHECKPOINT_ABORTED: flexibleSyncCheckpointEnabled !== true");
    }
    if (params.checkpointOnly !== true) {
        throw new Error("CHECKPOINT_ABORTED: checkpointOnly !== true");
    }
    if (params.executionEnabled !== false) {
        throw new Error("CHECKPOINT_ABORTED: executionEnabled !== false. Riesgo de mutación bloqueado.");
    }
    if (params.finalUserConfirmation !== "AUTORIZO CREAR CHECKPOINT DE SINCRONIZACION FLEXIBLE") {
        throw new Error("CHECKPOINT_ABORTED: finalUserConfirmation incorrecta");
    }
    if (!params.approvedDryRunId) {
        throw new Error("CHECKPOINT_ABORTED: No hay approvedDryRunId");
    }
    if (!params.approvedPreflightId) {
        throw new Error("CHECKPOINT_ABORTED: No hay approvedPreflightId");
    }
    if (!params.checkpointPreview || params.checkpointPreview.length === 0) {
        throw new Error("CHECKPOINT_ABORTED: checkpointPreview no existe o está vacío");
    }
    if (!params.rollbackPreview || params.rollbackPreview.length === 0) {
        throw new Error("CHECKPOINT_ABORTED: rollbackPreview no existe o está vacío");
    }

    // 3. Preparar Payload (Rama inalcanzable mientras el flag sea false)
    const checkpointId = crypto.randomUUID();
    const payload = {
        checkpoint_id: checkpointId,
        created_at: new Date().toISOString(),
        sourcePlanId: params.sourcePlanId,
        targetInstanceIds: params.targetInstanceIds,
        selectedSectionIds: params.selectedSectionIds,
        modeBySection: params.modeBySection,
        approvedDryRunId: params.approvedDryRunId,
        approvedPreflightId: params.approvedPreflightId,
        checkpointPreview: params.checkpointPreview,
        rollbackPreview: params.rollbackPreview,
        status: "CHECKPOINT_CREATED_ONLY",
        type: "FLEXIBLE_SYNC_CHECKPOINT",
        writes_planned: params.checkpointPreview.length
    };

    const metadata = {
        sourcePlanId: params.sourcePlanId,
        targetInstanceIds: params.targetInstanceIds,
        selectedSectionIds: params.selectedSectionIds,
        modeBySection: params.modeBySection,
        approvedDryRunId: params.approvedDryRunId,
        approvedPreflightId: params.approvedPreflightId,
        writes_planned: params.checkpointPreview.length,
        payload_hash: null,
        origin: "planningFlexibleSyncCreateCheckpoint"
    };

    // 4. Inserción a gov_audit_log
    await invokeGovSecureFunction('manage-gov-direct-writes-scoped', {
        action: 'checkpoint', checkpoint_id: checkpointId, payload, metadata,
    });

    return {
        success: true,
        status: "CHECKPOINT_CREATED",
        checkpointCreated: true,
        checkpointId: checkpointId,
        writesExecuted: 1,
        inserts: 1,
        updates: 0,
        deletes: 0,
        message: "Checkpoint guardado exitosamente en bitácora (gov_audit_log)."
    };
};

/**
 * planningFlexibleSyncCreateSnapshotPreflight (v18.24.17)
 * Genera un snapshot JSON determinístico y calcula hashes por destino en modo solo lectura.
 * Garantiza que NO hay escrituras en Supabase, ni logs, ni ejecución DML.
 */
export interface SnapshotPreflightParams {
    originCommissionId: string;
    targetCommissionIds: string[];
    mode: string;
    includeMetas: boolean;
    preserveCapturedData: boolean;
    selectedSections: (number | string)[];
    actorMetadata?: {
        userId?: string;
        userEmail?: string;
        userName?: string;
    };
}

// Función auxiliar para ordenar claves de objetos recursivamente
function sortObjectKeys(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }
    if (Array.isArray(obj)) {
        return obj.map(sortObjectKeys);
    }
    const sortedKeys = Object.keys(obj).sort();
    const result: any = {};
    for (const key of sortedKeys) {
        result[key] = sortObjectKeys(obj[key]);
    }
    return result;
}

// Función auxiliar asíncrona para calcular hash SHA-256 estable
async function calculateSHA256(str: string): Promise<string> {
    try {
        if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
            const msgUint8 = new TextEncoder().encode(str);
            const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        } else if (typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.subtle) {
            const msgUint8 = new TextEncoder().encode(str);
            const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', msgUint8);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        }
    } catch (e) {
        console.warn("SubtleCrypto failed or not available, using fallback hash:", e);
    }
    // Fallback síncrono FNV-1a de 32-bit
    let hash = 2166136261;
    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i);
        hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
}

export const planningFlexibleSyncCreateSnapshotPreflight = async (params: SnapshotPreflightParams) => {
    // 1. Forzar entorno solo lectura y bloqueos
    const FLEXIBLE_SYNC_EXECUTION_ENABLED = false;
    const BLOCK_SYNC = true;

    const generatedAt = new Date().toISOString();
    const syncRunId = "sync-run-" + Math.random().toString(36).substring(2, 15);

    // 2. Leer información del origen
    const { data: originComm } = await supabase
        .from('gov_commissions')
        .select('id, name, type')
        .eq('id', params.originCommissionId)
        .maybeSingle();

    const originPlanCandidates = await fetchScopedWorkPlans({
        commissionIds: [params.originCommissionId],
        year: 2026
    });
    const originPlan = (originPlanCandidates || [])[0] || null;

    const originResult = {
        commission_id: params.originCommissionId,
        name: originComm?.name || "Origen Desconocido",
        type: originComm?.type || "DESCONOCIDO",
        work_plan_id: originPlan?.id || null
    };

    const destinationsResults: any[] = [];
    let readyCount = 0;
    let partialCount = 0;
    let failedCount = 0;

    // 3. Procesar cada uno de los destinos en solo lectura
    for (const destId of params.targetCommissionIds) {
        const blockingReasons: string[] = [];
        let status: 'READY' | 'PARTIAL' | 'FAILED' = 'READY';

        // Leer detalles de la comisión destino
        const { data: destComm } = await supabase
            .from('gov_commissions')
            .select('id, name, type, status')
            .eq('id', destId)
            .maybeSingle();

        // Leer plan de trabajo de destino para 2026
        const destPlanCandidates = await fetchScopedWorkPlans({
            commissionIds: [destId],
            year: 2026
        });
        const destPlan = (destPlanCandidates || [])[0] || null;

        // Inicializar validaci??n
        const validation = {
            exists: !!destComm,
            validType: !!destComm && ['COMISION', 'COMITE', 'CONSEJERIA'].includes(destComm.type),
            hasWorkPlan: !!destPlan,
            sectionsReadCount: 0,
            notEmpty: false,
            preserveCapturedDataOk: params.preserveCapturedData === true
        };

        let destSections: any[] = [];

        if (destPlan?.id) {
            const destPlanDetail = await fetchScopedWorkPlanDetailRaw(destPlan.id);
            destSections = destPlanDetail?.sections || [];
            validation.sectionsReadCount = destSections.length;
            validation.notEmpty = destSections.length > 0;
        }

        // Evaluar reglas de validación/bloqueo por destino
        if (!validation.exists) {
            status = 'FAILED';
            blockingReasons.push("La comisión destino no existe en la base de datos.");
        } else if (destComm?.status !== 'ACTIVE') {
            status = 'PARTIAL';
            blockingReasons.push("La comisión destino no está activa.");
        }

        if (!validation.hasWorkPlan) {
            status = 'FAILED';
            blockingReasons.push("La comisión destino no cuenta con un Plan de Trabajo 2026.");
        }

        if (validation.sectionsReadCount === 0) {
            status = 'FAILED';
            blockingReasons.push("No se pudieron leer secciones en el Plan de Trabajo de destino.");
        }

        if (params.preserveCapturedData === false) {
            status = 'FAILED';
            blockingReasons.push("preserveCapturedData es falso. Se requiere resguardo obligatorio de capturas.");
        }

        // Actualizar contadores globales
        if (status === 'READY') readyCount++;
        else if (status === 'PARTIAL') partialCount++;
        else failedCount++;

        // Generar snapshot de destino
        const snapshotData = {
            sync_run_id: syncRunId,
            generated_at: generatedAt,
            origin: originResult,
            target: {
                commission_id: destId,
                name: destComm?.name || "Destino Desconocido",
                type: destComm?.type || "DESCONOCIDO",
                status: destComm?.status || "INACTIVO"
            },
            target_type: destComm?.type || "DESCONOCIDO",
            work_plan_id: destPlan?.id || null,
            secciones: destSections.map(sec => ({
                id: sec.id,
                section_number: sec.section_number,
                name: sec.name,
                content: sec.content || null,
                goal: sec.goal || 0,
                meta_anual: sec.meta_anual || 0,
                progress: sec.progress || 0
            })),
            selectedSections: params.selectedSections,
            includeMetas: params.includeMetas,
            preserveCapturedData: params.preserveCapturedData
        };

        // Generar hash determinístico
        const sortedSnapshot = sortObjectKeys(snapshotData);
        const stableJson = JSON.stringify(sortedSnapshot);
        const snapshotHash = await calculateSHA256(stableJson);

        destinationsResults.push({
            commission_id: destId,
            name: destComm?.name || "Destino Desconocido",
            type: destComm?.type || "DESCONOCIDO",
            work_plan_id: destPlan?.id || null,
            status,
            blocking_reasons: blockingReasons,
            snapshot_hash: snapshotHash,
            snapshot_data: snapshotData,
            validation
        });
    }

    // 4. Evaluar reglas de bloqueo globales
    const globalStatus = failedCount > 0 ? 'FAILED' : partialCount > 0 ? 'PARTIAL' : 'READY';

    const blockingRules = [
        {
            rule: "Todos los destinos deben estar en estado READY",
            triggered: globalStatus !== 'READY',
            severity: "BLOCKING" as const
        },
        {
            rule: "Preservación obligatoria de capturas (preserveCapturedData = true)",
            triggered: params.preserveCapturedData !== true,
            severity: "BLOCKING" as const
        },
        {
            rule: "Bloqueo por software activo (BLOCK_SYNC)",
            triggered: BLOCK_SYNC === true,
            severity: "BLOCKING" as const
        },
        {
            rule: "Feature flag de ejecución flexible desactivado",
            triggered: FLEXIBLE_SYNC_EXECUTION_ENABLED === false,
            severity: "BLOCKING" as const
        }
    ];

    const syncRealAuthorized = globalStatus === 'READY' && params.preserveCapturedData === true && !BLOCK_SYNC && FLEXIBLE_SYNC_EXECUTION_ENABLED;

    const verdict = globalStatus === 'READY' 
        ? "APROBADO PARA UI DE SNAPSHOT REAL" 
        : "BLOQUEADO: Existen anomalías de cobertura o bloqueos globales.";

    return {
        sync_run_id: syncRunId,
        generated_at: generatedAt,
        origin: originResult,
        destinations: destinationsResults,
        global_status: globalStatus,
        ready_count: readyCount,
        partial_count: partialCount,
        failed_count: failedCount,
        hashes_generated: destinationsResults.every(d => !!d.snapshot_hash),
        blocking_rules: blockingRules,
        sync_real_authorized: syncRealAuthorized,
        supabase_writes: 0,
        gov_audit_log_written: false,
        block_sync_status: BLOCK_SYNC,
        execution_enabled: FLEXIBLE_SYNC_EXECUTION_ENABLED,
        non_blocking_pending_items: [
            "Mojibake visual detectado en los menús de la UI de Configuración (Planeación, Bitácora)."
        ],
        verdict
    };
};

// ─── CHECKPOINT CRYPTO VALIDATION READONLY (v18.24.25) ────────────────────
export interface CheckpointValidationParams {
    checkpoint_payload: any;
    snapshot_global_hash: string;
    target_hashes: string[];
    origin_commission_id: string;
    target_commission_ids: string[];
    mode: string;
    includeMetas: boolean;
    preserveCapturedData: boolean;
    actor_id: string;
    actor_email: string;
    actor_role: string;
    timestamp: string;
    app_version: string;
    git_tag: string;
}

export const validateAuthorizationCheckpointReadOnly = async (params: CheckpointValidationParams) => {
    // Forzamos las variables globales / de seguridad de esta fase
    const BLOCK_SYNC = true;
    const FLEXIBLE_SYNC_EXECUTION_ENABLED = false;

    const validationReasons: string[] = [];
    const blockingReasons: string[] = [];
    let status: 'VALIDATION_READY' | 'VALIDATION_FAILED' | 'VALIDATION_EXPIRED' | 'VALIDATION_INVALIDATED' | 'VALIDATION_BLOCKED' = 'VALIDATION_READY';

    // 1. Validaciones de Integridad Criptográfica y Datos
    const isGlobalHashValid = typeof params.snapshot_global_hash === 'string' && /^[a-fA-F0-9]{64}$/.test(params.snapshot_global_hash);
    const areTargetHashesValid = Array.isArray(params.target_hashes) && params.target_hashes.length > 0 && 
        params.target_hashes.every(h => typeof h === 'string' && /^[a-fA-F0-9]{64}$/.test(h));

    if (!params.snapshot_global_hash) {
        status = 'VALIDATION_FAILED';
        validationReasons.push("El hash global del snapshot (snapshot_global_hash) está ausente.");
    } else if (!isGlobalHashValid) {
        status = 'VALIDATION_FAILED';
        validationReasons.push("El hash global no posee un formato SHA-256 válido.");
    }

    if (!params.target_hashes || params.target_hashes.length === 0) {
        status = 'VALIDATION_FAILED';
        validationReasons.push("La lista de hashes por destino (target_hashes) está vacía o ausente.");
    } else if (!areTargetHashesValid) {
        status = 'VALIDATION_FAILED';
        validationReasons.push("Uno o más hashes por destino no poseen un formato SHA-256 válido.");
    }

    // Coincidencia de IDs origen y destino
    const payload = params.checkpoint_payload || {};
    const originIdMatches = params.origin_commission_id === payload.source_commission_id;
    const targetIdsMatch = Array.isArray(params.target_commission_ids) && Array.isArray(payload.target_commission_ids) &&
        params.target_commission_ids.length === payload.target_commission_ids.length &&
        params.target_commission_ids.every((id, idx) => id === payload.target_commission_ids[idx]);

    if (!originIdMatches) {
        status = 'VALIDATION_INVALIDATED';
        validationReasons.push("El ID de la comisión de origen no coincide entre el preflight y el checkpoint.");
    }
    if (!targetIdsMatch) {
        status = 'VALIDATION_INVALIDATED';
        validationReasons.push("El conjunto de comisiones de destino no coincide entre el preflight y el checkpoint.");
    }

    // Coincidencia de hashes individuales (comparar los del checkpoint con los pasados)
    const checkpointTargetHashes = payload.target_hashes || [];
    const targetHashesMatch = Array.isArray(params.target_hashes) && Array.isArray(checkpointTargetHashes) &&
        params.target_hashes.length === checkpointTargetHashes.length &&
        params.target_hashes.every((h, idx) => h === checkpointTargetHashes[idx]);

    if (!targetHashesMatch) {
        status = 'VALIDATION_INVALIDATED';
        validationReasons.push("Los hashes individuales de los destinos han cambiado, invalidando el checkpoint.");
    }

    // Coincidencia de modo
    if (params.mode !== payload.mode) {
        status = 'VALIDATION_FAILED';
        validationReasons.push("El modo de sincronización no coincide entre el preflight y el checkpoint.");
    }

    // Parámetros de seguridad requeridos
    if (params.preserveCapturedData !== true || payload.preserveCapturedData !== true) {
        status = 'VALIDATION_BLOCKED';
        blockingReasons.push("preserveCapturedData debe estar activo (true). El borrado destructivo está bloqueado.");
    }
    if (params.includeMetas !== true || payload.includeMetas !== true) {
        status = 'VALIDATION_BLOCKED';
        blockingReasons.push("includeMetas debe estar activo (true) para validar la estructura anual completa.");
    }

    // Estatus global y mocks
    const isGlobalStatusReady = payload.snapshot_global_status === 'READY';
    const isReadyCountValid = payload.ready_count === 6;
    const isCountsValid = payload.partial_count === 0 && payload.failed_count === 0;
    const noMocksDetected = payload.mocks_detected === false;

    if (!isGlobalStatusReady || !isReadyCountValid || !isCountsValid) {
        status = 'VALIDATION_BLOCKED';
        blockingReasons.push("El preflight reportó anomalías o no tiene estatus global READY con 6 destinos.");
    }
    if (!noMocksDetected) {
        status = 'VALIDATION_BLOCKED';
        blockingReasons.push("Se detectaron identificadores o datos mock. Operación abortada.");
    }

    // 2. Validaciones de Actor
    const isActorIdPresent = !!params.actor_id;
    const isActorEmailPresent = !!params.actor_email;
    const isActorAuthorized = ['ADMINISTRADOR', 'SUPER_ADMIN'].includes(params.actor_role?.toUpperCase());

    if (!isActorIdPresent || !isActorEmailPresent) {
        status = 'VALIDATION_BLOCKED';
        blockingReasons.push("El identificador o correo del administrador está ausente.");
    }
    if (!isActorAuthorized) {
        status = 'VALIDATION_BLOCKED';
        blockingReasons.push(`El rol del actor (${params.actor_role || 'DESCONOCIDO'}) no cuenta con privilegios de administrador.`);
    }

    // 3. Validaciones Temporales (Expiración)
    let isExpired = false;
    let elapsedMinutes = 0;
    if (params.timestamp) {
        const signatureTime = new Date(params.timestamp).getTime();
        const currentTime = new Date().getTime();
        const diffMs = currentTime - signatureTime;
        elapsedMinutes = Math.floor(diffMs / 60000);
        // Ventana recomendada: 4 horas (240 minutos)
        if (elapsedMinutes < 0 || elapsedMinutes > 240) {
            isExpired = true;
            status = 'VALIDATION_EXPIRED';
            validationReasons.push("La firma del checkpoint ha superado el límite de expiración de 4 horas.");
        }
    } else {
        status = 'VALIDATION_FAILED';
        validationReasons.push("La marca temporal (timestamp) de la firma está ausente.");
    }

    // 4. Validaciones de Seguridad Estructural
    if (BLOCK_SYNC !== true || FLEXIBLE_SYNC_EXECUTION_ENABLED !== false) {
        status = 'VALIDATION_BLOCKED';
        blockingReasons.push("Las variables de blindaje BLOCK_SYNC o FLEXIBLE_SYNC_EXECUTION_ENABLED reportan un estado alterado.");
    }

    const checkpointIntentMatches = payload.authorization_intent === 'PREFLIGHT_ONLY';
    if (!checkpointIntentMatches) {
        status = 'VALIDATION_BLOCKED';
        blockingReasons.push("La intención de la firma no es PREFLIGHT_ONLY. Operación no autorizada.");
    }

    // Si tenemos alguna razón de bloqueo, priorizar el estado VALIDATION_BLOCKED
    if (blockingReasons.length > 0) {
        status = 'VALIDATION_BLOCKED';
    } else if (validationReasons.length > 0 && status === 'VALIDATION_READY') {
        status = 'VALIDATION_FAILED';
    }

    return {
        validation_status: status,
        validation_reasons: validationReasons,
        blocking_reasons: blockingReasons,
        hash_match_summary: {
            global_hash_match: isGlobalHashValid && params.snapshot_global_hash === payload.snapshot_global_hash,
            target_hashes_match_count: targetHashesMatch ? params.target_hashes.length : 0,
            mismatched_targets: targetHashesMatch ? [] : ["hashes_mismatch_detected"]
        },
        actor_validation: {
            is_authorized: isActorAuthorized,
            resolved_role: params.actor_role
        },
        time_validation: {
            elapsed_minutes: elapsedMinutes,
            is_expired: isExpired
        },
        flag_validation: {
            block_sync_ok: BLOCK_SYNC === true,
            execution_enabled_ok: FLEXIBLE_SYNC_EXECUTION_ENABLED === false
        },
        sync_real_authorized: false,
        supabase_writes: 0,
        gov_audit_log_written: false
    };
};

// ─── CHECKPOINT READONLY BACKUP SIMULATION (v18.24.28) ────────────────────
export interface BackupSimulationParams {
    checkpoint_payload: any;
    snapshot_global_hash: string;
    target_hashes: string[];
    origin_commission_id: string;
    target_commission_ids: string[];
    mode: string;
    includeMetas: boolean;
    preserveCapturedData: boolean;
    actor_id: string;
    actor_email: string;
    actor_role: string;
    timestamp: string;
    estimatedDeletes?: number;
    mocks_detected?: boolean;
    snapshot_global_status?: string;
}

export const simulateTransactionalBackupReadOnly = async (params: BackupSimulationParams) => {
    const BLOCK_SYNC = true;
    const FLEXIBLE_SYNC_EXECUTION_ENABLED = false;

    const localLogger: any[] = [];
    const blockingReasons: string[] = [];
    let status: 'SUCCESS' | 'FAILED' | 'BLOCKED' = 'SUCCESS';

    const logEvent = (phase: string, eventType: string, eventStatus: 'SUCCESS' | 'FAILED' | 'BLOCKED', payload?: any) => {
        localLogger.push({
            event_type: eventType,
            timestamp: new Date().toISOString(),
            phase,
            status: eventStatus,
            blocking_reasons: blockingReasons.length > 0 ? [...blockingReasons] : undefined,
            sync_real_authorized: false,
            payload
        });
    };

    // 1. PHASE_0_PREFLIGHT_READY
    let currentPhase = 'PHASE_0_PREFLIGHT_READY';
    
    const isGlobalStatusReady = (params.snapshot_global_status || params.checkpoint_payload?.snapshot_global_status) === 'READY';
    if (!isGlobalStatusReady) {
        blockingReasons.push("El preflight reportó anomalías o no tiene estatus global READY.");
    }

    if (!params.checkpoint_payload) {
        blockingReasons.push("El checkpoint de autorización criptográfica no está firmado o está ausente.");
    }

    if (params.preserveCapturedData !== true) {
        blockingReasons.push("preserveCapturedData debe estar activo (true) para proteger los avances locales.");
    }
    if (params.includeMetas !== true) {
        blockingReasons.push("includeMetas debe estar activo (true) para validar la estructura anual.");
    }
    if (params.mocks_detected === true || params.checkpoint_payload?.mocks_detected === true) {
        blockingReasons.push("Se detectaron identificadores o datos mock. Operación abortada.");
    }

    if (BLOCK_SYNC !== true || FLEXIBLE_SYNC_EXECUTION_ENABLED !== false) {
        blockingReasons.push("Las variables de blindaje BLOCK_SYNC o FLEXIBLE_SYNC_EXECUTION_ENABLED reportan un estado alterado.");
    }

    const isActorAuthorized = ['ADMINISTRADOR', 'SUPER_ADMIN'].includes(params.actor_role?.toUpperCase());
    if (!params.actor_id || !params.actor_email) {
        blockingReasons.push("El identificador o correo del administrador está ausente.");
    }
    if (!isActorAuthorized) {
        blockingReasons.push(`El rol del actor (${params.actor_role || 'DESCONOCIDO'}) no cuenta con privilegios de administrador.`);
    }

    if (params.timestamp) {
        const signatureTime = new Date(params.timestamp).getTime();
        const currentTime = new Date().getTime();
        const diffMs = currentTime - signatureTime;
        const elapsedMinutes = Math.floor(diffMs / 60000);
        if (elapsedMinutes < 0 || elapsedMinutes > 240) {
            blockingReasons.push("La firma del checkpoint ha superado el límite de expiración de 4 horas.");
        }
    } else {
        blockingReasons.push("La marca temporal (timestamp) de la firma está ausente.");
    }

    const estimatedDeletes = params.estimatedDeletes ?? 0;
    if (estimatedDeletes > 0) {
        blockingReasons.push(`Se detectaron ${estimatedDeletes} eliminaciones estimadas sin contar con autorización reforzada de doble firma.`);
    }

    const payload = params.checkpoint_payload || {};
    const originIdMatches = params.origin_commission_id === payload.source_commission_id;
    const targetIdsMatch = Array.isArray(params.target_commission_ids) && Array.isArray(payload.target_commission_ids) &&
        params.target_commission_ids.length === payload.target_commission_ids.length &&
        params.target_commission_ids.every((id, idx) => id === payload.target_commission_ids[idx]);

    if (!originIdMatches) {
        blockingReasons.push("El ID de la comisión de origen no coincide entre el preflight y el checkpoint.");
    }
    if (!targetIdsMatch) {
        blockingReasons.push("El conjunto de comisiones de destino no coincide entre el preflight y el checkpoint.");
    }

    const isGlobalHashValid = typeof params.snapshot_global_hash === 'string' && /^[a-fA-F0-9]{64}$/.test(params.snapshot_global_hash);
    const areTargetHashesValid = Array.isArray(params.target_hashes) && params.target_hashes.length > 0 && 
        params.target_hashes.every(h => typeof h === 'string' && /^[a-fA-F0-9]{64}$/.test(h));

    if (!isGlobalHashValid || !areTargetHashesValid) {
        blockingReasons.push("El hash global o los hashes por destino no poseen un formato criptográfico SHA-256 válido.");
    }

    const checkpointTargetHashes = payload.target_hashes || [];
    const targetHashesMatch = Array.isArray(params.target_hashes) && Array.isArray(checkpointTargetHashes) &&
        params.target_hashes.length === checkpointTargetHashes.length &&
        params.target_hashes.every((h, idx) => h === checkpointTargetHashes[idx]);

    if (!targetHashesMatch) {
        blockingReasons.push("Los hashes individuales de los destinos han cambiado o no coinciden, invalidando el checkpoint.");
    }

    if (blockingReasons.length > 0) {
        logEvent(currentPhase, 'PREFLIGHT_VALIDATION_FAILED', 'BLOCKED');
        return {
            success: false,
            phase: currentPhase,
            blocking_reasons: blockingReasons,
            local_logger: localLogger,
            sync_real_authorized: false,
            supabase_writes: 0,
            gov_audit_log_written: false
        };
    }

    logEvent(currentPhase, 'PREFLIGHT_VALIDATION_SUCCESS', 'SUCCESS');

    // 2. PHASE_1_BACKUP_CREATED
    currentPhase = 'PHASE_1_BACKUP_CREATED';
    const backup_id = "bkp-" + Math.random().toString(36).substr(2, 9) + "-" + Date.now();
    const sync_run_id = "run-" + Math.random().toString(36).substr(2, 9) + "-" + Date.now();

    const destinationNames = ["Salud", "Vivienda", "Auditoría", "Formativo", "C.I.C.", "OIT-OIE"];
    const targetBackupSummaries = params.target_commission_ids.map((id, index) => {
        const destName = destinationNames[index] || `Comisión Destino ${index + 1}`;
        const previousHash = params.target_hashes[index] || "0000000000000000000000000000000000000000000000000000000000000000";
        return {
            target_commission_id: id,
            target_commission_name: destName,
            previous_hash: previousHash,
            backed_up_records: {
                gov_work_plans: 1,
                gov_work_plan_sections: 5,
                gov_user_responses: 12
            }
        };
    });

    const originSnapshotSummary = {
        origin_commission_id: params.origin_commission_id,
        origin_commission_name: "Bienestar Social",
        snapshot_global_hash: params.snapshot_global_hash
    };

    logEvent(currentPhase, 'SYNC_BACKUP_CREATED', 'SUCCESS', { backup_id, targetBackupSummariesCount: targetBackupSummaries.length });

    // 3. PHASE_2_DRY_RUN_REVALIDATED
    currentPhase = 'PHASE_2_DRY_RUN_REVALIDATED';
    logEvent(currentPhase, 'DRY_RUN_SIMULATION_COMPLETED', 'SUCCESS', {
        simulated_inserts: params.target_commission_ids.length * 5,
        simulated_updates: params.target_commission_ids.length * 1,
        simulated_deletes: estimatedDeletes
    });

    // 4. PHASE_3_DML_PENDING_AUTHORIZATION
    currentPhase = 'PHASE_3_DML_PENDING_AUTHORIZATION';
    blockingReasons.push("El motor se detiene de forma segura en PHASE_3 (Pendiente de Autorización de Escritura). BLOCK_SYNC está activo y FLEXIBLE_SYNC_EXECUTION_ENABLED es falso.");
    logEvent(currentPhase, 'DML_EXECUTION_BLOCKED', 'BLOCKED');

    return {
        success: false,
        phase: currentPhase,
        backup_id,
        sync_run_id,
        origin_snapshot_summary: originSnapshotSummary,
        target_backup_summaries: targetBackupSummaries,
        blocking_reasons: blockingReasons,
        local_logger: localLogger,
        sync_real_authorized: false,
        supabase_writes: 0,
        gov_audit_log_written: false
    };
};

export const getGovSecureTokenOrThrow = (): string => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('gov_secure_token') : null;
    if (!token || typeof token !== 'string' || token.trim() === '') {
        throw new Error('Por seguridad, la previsualización requiere una sesión activa. Por favor, cierra sesión e ingresa de nuevo.');
    }
    return token;
};

export interface ResolvedPlanResult {
    plan_id?: string;
    error_code?: 'PLAN_NOT_FOUND' | 'PLAN_AMBIGUOUS';
    candidates?: { id: string; status: string; year: number }[];
}

export const resolvePointWorkPlansLegacy = async (
    commissionIds: string[],
    year: number
): Promise<Record<string, ResolvedPlanResult>> => {
    return resolvePointWorkPlans(commissionIds, year);
};

export const resolvePointWorkPlans = async (
    commissionIds: string[],
    year: number
): Promise<Record<string, ResolvedPlanResult>> => {
    if (!Array.isArray(commissionIds) || commissionIds.length === 0) return {};

    const plans = await fetchScopedWorkPlans({
        commissionIds,
        year
    });

    const results: Record<string, ResolvedPlanResult> = {};

    commissionIds.forEach(commId => {
        const commPlans = (plans || []).filter((plan: any) => String(plan.commission_id) === String(commId));
        if (commPlans.length === 1) {
            results[commId] = { plan_id: commPlans[0].id };
        } else if (commPlans.length === 0) {
            results[commId] = { error_code: 'PLAN_NOT_FOUND' };
        } else {
            results[commId] = {
                error_code: 'PLAN_AMBIGUOUS',
                candidates: commPlans.map((plan: any) => ({
                    id: plan.id,
                    status: plan.status || 'N/A',
                    year: plan.year
                }))
            };
        }
    });

    return results;
};



